
import React, { useState, useRef, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import axios from "axios";
import { BASE_URL } from "../../config/axios";
import { ToastContainer, toast } from 'react-toastify';
import usePermission from "../../config/permissions";
import * as XLSX from 'xlsx';
import { handleErrorToast, sortProjects, CSSForSelect, CSSForMultiSelectOption, CSSForSelect23vWWidth, CSSForSelect15vWWidth, CSSForSelectModern, ProgressModal } from "../CustomFunctions";
import { AddwithWhiteCircle, DeleteDustbin, Tripledot, UpdatePencil, DropdownArrowOption, Attachment, CircularGrid } from "../AllSvg";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { Activity, AlertCircle, AlertTriangle, Briefcase, Building, Building2, Check, ChevronRight, CreditCard, Download, FileText, Hash, IndianRupee, Info, Landmark, Loader, Mail, MapPin, MoreVertical, Paperclip, Phone, Save, Settings, ShieldCheck, Tag, Trash2, Upload, UploadCloud, User, UserCog, Users, X, Zap } from "lucide-react";
// import '../employeeCard.css';
import { Calendar } from "antd";
import AddModalButton from "../SiteManagement/Components/modernbuttons/AddButton";

// ! *************** Designation Crud Models Start ***************

const AddSiteDepartment = ({ getSiteDepartment, siteDepartmentList, divisionList }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => {
        setFormData({
            title: "",
            division: "",
            sub_company: "",
            professional_type: "null",
        });
        setErrors({});
        setShow(false);
    };
    const handleShow = () => setShow(true);

    const [formData, setFormData] = useState({
        title: "",
        division: "",
        sub_company: "",
        professional_type: "null",
    });

    useEffect(() => {
        if (formData.division) {
            const filterSubCompany = divisionList.filter(i => i.id === formData.division);
            setFormData((prev) => ({
                ...prev,
                sub_company: filterSubCompany[0].sub_company
            }));
        }
    }, [formData.division]);

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["title", "division", "professional_type"];
        requiredFields.forEach((field) => {
            if (!formData[field] || formData[field] === "null") {
                newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required!`;
            }
        });
        setErrors(newErrors);
        setIsSubmitted(true);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setInputState({ ...inputState, [name]: value.trim() ? "green" : "" });
        setFormData({ ...formData, [name]: value });
        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (value) delete updatedErrors[name];
            return updatedErrors;
        });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!(validateForm() && formData.title != null)) return;

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            let res = await axios.post(`${BASE_URL}/siteproject/designation/`, formData);
            if (res.status === 200) {
                await getSiteDepartment();
                toast.dismiss(loadingToastId);
                toast.success("Designation Added Successfully");
                handleClose();
            } else {
                alert(res);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    const professionalTypeList = [
        { value: "Key Professional", label: "Key Professional" },
        { value: "Sub Professional", label: "Sub Professional" },
        { value: "Support Staff", label: "Support Staff" }
    ];

    const professionalTypeOptions = professionalTypeList.map((i) => ({ value: i.value, label: i.label }));

    const handleProfessionalTypeChange = (selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            professional_type: selectedOption ? selectedOption.value : "null",
        }));
        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (selectedOption) delete updatedErrors["professional_type"];
            return updatedErrors;
        });
    };

    const divisionOptions = divisionList.map((company) => ({ value: company.id, label: company.name }));

    const handleDivisionChange = (selectedOption) => {
        handleInputChange({ target: { name: "division", value: selectedOption ? selectedOption.value : "" } });
    };

    return (
        <>
            <button title="Add Site Designation" className="btn-animated" onClick={handleShow}>
                <AddwithWhiteCircle /> Site Designation
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Add" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="md">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <Tag size={22} />
                            </div>
                            <div>
                                <Modal.Title>Add Site Designation</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>Create a new designation for your team</p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

                    <Modal.Body className="modern-modal-body">
                        <div className="form-section">
                            <div className="section-title">
                                <Tag size={18} />
                                <h4>Designation Details</h4>
                            </div>

                            <div className="form-grid">
                                <div className="form-field full-width">
                                    <label><FileText size={14} /> Site Designation<span className="required">*</span></label>
                                    <input
                                        maxLength={50}
                                        type="text"
                                        name="title"
                                        placeholder="Site Designation Name"
                                        onChange={handleInputChange}
                                        value={formData.title}
                                        className={errors.title ? "error" : inputState.title ? "success" : ""}
                                    />
                                    {errors.title && <span className="error-message">{errors.title}</span>}
                                </div>

                                <div className="form-field">
                                    <label><Briefcase size={14} /> Professional Type<span className="required">*</span></label>
                                    <Select
                                        options={professionalTypeOptions}
                                        value={professionalTypeOptions.find((option) => option.value === formData.professional_type) || null}
                                        onChange={handleProfessionalTypeChange}
                                        placeholder="Select Professional Type"
                                        styles={CSSForSelectModern}
                                    />
                                    {errors.professional_type && <span className="error-message">{errors.professional_type}</span>}
                                </div>

                                <div className="form-field">
                                    <label><Building size={14} /> Division<span className="required">*</span></label>
                                    <Select
                                        options={divisionOptions}
                                        isSearchable
                                        isClearable
                                        placeholder="Select Division"
                                        value={divisionOptions.find((option) => option.value === formData.division) || null}
                                        onChange={handleDivisionChange}
                                        styles={CSSForSelectModern}
                                    />
                                    {errors.division && <span className="error-message">{errors.division}</span>}
                                </div>
                            </div>

                            <div className="form-actions">
                                <div></div>
                                <div className="submit-buttons">
                                    <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-primaryy"
                                        onClick={handleFormSubmit}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={18} className="spinning" /> : <Save size={18} />}
                                        <span>{loading ? 'Adding...' : 'Add Designation'}</span>
                                    </button>
                                </div>
                            </div>

                            <p className="error-message" style={{ marginTop: '12px' }}>
                                {isSubmitted && Object.keys(errors).length > 0 && (
                                    Object.keys(errors).length > 5 ? (
                                        <span>Please fill all mandatory fields!</span>
                                    ) : (
                                        Object.keys(errors).map((field) => field.charAt(0).toUpperCase() + field.slice(1)).join(', ') + ' are required!'
                                    )
                                )}
                            </p>
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const UpdateSiteDepartment = ({ i, getSiteDepartment, siteDepartmentList, divisionList }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData(i);
        setShow(true);
    };

    const [filteredList, setFilteredList] = useState([]);

    const [formData, setFormData] = useState({
        title: "",
        division: "",
        sub_company: "",
        professional_type: "null",
    });

    useEffect(() => {
        if (formData.division) {
            const filterSubCompany = divisionList.filter(i => i.id === formData.division);
            setFormData((prev) => ({
                ...prev,
                sub_company: filterSubCompany[0]?.sub_company
            }));
        }
    }, [formData.division]);

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["title", "division", "professional_type"];
        requiredFields.forEach((field) => {
            if (!formData[field] || formData[field] === "null") {
                newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required!`;
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setInputState({ ...inputState, [name]: value.trim() ? "green" : "" });
        setFormData({ ...formData, [name]: value });

        if (name === 'title') {
            const filtered = siteDepartmentList.filter(item =>
                item.title.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredList(filtered);
        }

        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (value) delete updatedErrors[name];
            return updatedErrors;
        });
    };

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            let res = await axios.put(`${BASE_URL}/siteproject/designation/${i.id}/`, formData);
            if (res.status === 200) {
                await getSiteDepartment();
                toast.dismiss(loadingToastId);
                toast.success("Designation Updated Successfully");
                setShow(false);
            } else {
                alert(res);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    const professionalTypeList = [
        { value: "Key Professional", label: "Key Professional" },
        { value: "Sub Professional", label: "Sub Professional" },
        { value: "Support Staff", label: "Support Staff" }
    ];

    const professionalTypeOptions = professionalTypeList.map((i) => ({ value: i.value, label: i.label }));

    const handleProfessionalTypeChange = (selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            professional_type: selectedOption?.value || null,
        }));
        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (selectedOption) delete updatedErrors.professional_type;
            return updatedErrors;
        });
    };

    const divisionOptions = divisionList.map((company) => ({ value: company.id, label: company.name }));

    const handleDivisionChange = (selectedOption) => {
        handleInputChange({ target: { name: "division", value: selectedOption ? selectedOption.value : "" } });
    };

    return (
        <>
            <button title="Update Site Designation" className="model-delete-button-modern" style={{ backgroundColor: "#00000075" }} onClick={handleShow}>
                <UpdatePencil modernButton="true" />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Update" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="md">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <Tag size={22} />
                            </div>
                            <div>
                                <Modal.Title>Update Designation</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>{i.title}</p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

                    <Modal.Body className="modern-modal-body">
                        <div className="form-section">
                            <div className="section-title">
                                <Tag size={18} />
                                <h4>Designation Details</h4>
                            </div>

                            <div className="form-grid">
                                <div className="form-field full-width">
                                    <label><FileText size={14} /> Title<span className="required">*</span></label>
                                    <input
                                        maxLength={50}
                                        type="text"
                                        name="title"
                                        placeholder="Designation Name"
                                        onChange={handleInputChange}
                                        value={formData.title}
                                        className={errors.title ? "error" : inputState.title ? "success" : ""}
                                    />
                                    {errors.title && <span className="error-message">{errors.title}</span>}
                                    {filteredList.length > 0 && formData.title !== i.title && (
                                        <div className="list-group" style={{ marginTop: '6px' }}>
                                            {filteredList.map((item, index) => (
                                                <div key={index} className="list-group-item">
                                                    {item.title}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="form-field">
                                    <label><Briefcase size={14} /> Professional Type<span className="required">*</span></label>
                                    <Select
                                        isSearchable
                                        isClearable
                                        options={professionalTypeOptions}
                                        value={professionalTypeOptions.find((option) => option.value === formData.professional_type) || null}
                                        onChange={handleProfessionalTypeChange}
                                        placeholder="Select Professional Type"
                                        styles={CSSForSelectModern}
                                    />
                                    {errors.professional_type && <span className="error-message">{errors.professional_type}</span>}
                                </div>

                                <div className="form-field">
                                    <label><CircularGrid size={14} /> Division<span className="required">*</span></label>
                                    <Select
                                        options={divisionOptions}
                                        isSearchable
                                        isClearable
                                        placeholder="Select Division"
                                        value={divisionOptions.find((option) => option.value === formData.division) || null}
                                        onChange={handleDivisionChange}
                                        styles={CSSForSelectModern}
                                    />
                                    {errors.division && <span className="error-message">{errors.division}</span>}
                                </div>
                            </div>

                            <div className="form-actions">
                                <div></div>
                                <div className="submit-buttons">
                                    <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-primaryy"
                                        onClick={handleFormSubmitPut}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={18} className="spinning" /> : <Save size={18} />}
                                        <span>{loading ? 'Updating...' : 'Update'}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const DeleteSiteDepartment = ({ i, getSiteDepartment }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const hasEmployees = i.employee_count > 0;

    const handleFormSubmitDelete = async (e) => {
        e.preventDefault();
        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            let res = await axios.delete(`${BASE_URL}/siteproject/designation/${i.id}/`);
            if (res.status === 200) {
                await getSiteDepartment();
                toast.dismiss(loadingToastId);
                toast.success("Deleted Successfully");
                setShow(false);
            } else {
                alert(res);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <button title="Delete Site Designation" className="model-delete-button-modern" style={{ backgroundColor: "#00000075" }} onClick={handleShow}>
                <DeleteDustbin modernButton="true" />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Delete" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-required-modal" centered>
                    <Modal.Header className="modern-modal-header requirement-modal-header">
                        <div className="header-content">
                            <div className="requirement-header-icon">
                                {hasEmployees ? <AlertTriangle size={22} /> : <Trash2 size={22} />}
                            </div>
                            <div>
                                <Modal.Title>{hasEmployees ? "Cannot Delete" : "Delete Designation"}</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {hasEmployees ? "Employees are still assigned" : "This action cannot be undone"}
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body requirement-modal-body">
                        <div className="requirement-mode-summary" style={{ marginBottom: '20px' }}>
                            <AlertCircle size={16} />
                            {hasEmployees ? (
                                <span>
                                    <strong>{i.employee_count}</strong> employee{i.employee_count !== 1 ? 's are' : ' is'} assigned to <strong>{i.title}</strong>. Please reassign {i.employee_count !== 1 ? 'them' : 'them'} to a different designation before deleting.
                                </span>
                            ) : (
                                <span>
                                    Are you sure you want to delete <strong>{i.title}</strong>?
                                </span>
                            )}
                        </div>

                        <div className="submit-buttons" style={{ justifyContent: 'flex-end' }}>
                            <button type="button" className="btn-secondaryy" onClick={handleClose} disabled={loading}>
                                {hasEmployees ? "Close" : "Cancel"}
                            </button>
                            {!hasEmployees && (
                                <button
                                    type="button"
                                    className="btn-primaryy"
                                    style={{ background: "#dc3545", borderColor: "#dc3545" }}
                                    onClick={handleFormSubmitDelete}
                                    disabled={loading}
                                >
                                    {loading ? <Loader size={18} className="spinning" /> : <Trash2 size={18} />}
                                    <span>{loading ? 'Deleting...' : 'Delete'}</span>
                                </button>
                            )}
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

// const AddSiteSubcompanys = ({ getSiteDeop }) => {

//     const [show, setShow] = useState(false);

//     const handleClose = () => {
//         setFormData({
//             title: ""
//         })
//         setErrors({});
//         setShow(false);
//     };

//     const handleShow = () => setShow(true);

//     const [formData, setFormData] = useState({
//         title: "",
//     });

//     // !  ************** Validation start **************  ! //

//     const [errors, setErrors] = useState({});
//     const [inputState, setInputState] = useState({});
//     const [isSubmitted, setIsSubmitted] = useState(false);

//     const validateForm = () => {
//         const newErrors = {};
//         const requiredFields = ["title"];
//         requiredFields.forEach((field) => {
//             if (!formData[field]) {
//                 newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
//                     } is required !`;
//             }
//         });

//         setErrors(newErrors);
//         setIsSubmitted(true);
//         return Object.keys(newErrors).length === 0;
//     };

//     const handleInputChange = (e) => {
//         const { name, value } = e.target;

//         setInputState({
//             ...inputState,
//             [name]: value.trim() ? "green" : "",
//         });

//         setFormData({
//             ...formData,
//             [name]: value,
//         });

//         setErrors((prevErrors) => {
//             const updatedErrors = { ...prevErrors };
//             if (value) {
//                 delete updatedErrors[name];
//             }
//             return updatedErrors;
//         });
//     };

//     // ?  ************** Validation End **************  ! //
//     const [loading, setLoading] = useState(false); //loading logic
//     const handleFormSubmit = async (e) => {
//         e.preventDefault();

//         if (validateForm()) {
//             const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
//             setLoading(true); //loading logic
//             try {
//                 let res = await axios.post(`${BASE_URL}/siteproject/subcompany/`, formData);

//                 if (res.status === 200) {
//                     handleClose();
//                     await getSiteDeop();
//                 } else {
//                     alert(res);
//                 }
//             } catch (err) {
//                 handleErrorToast(err);
//             } finally {
//                 setLoading(false); //loading logic
//                 toast.dismiss(loadingToastId);
//             }
//         }
//     };

//     return (
//         <>
//             <button title="Add Sub Company" className="upload-svg" onClick={handleShow}>
//                 <AddwithWhiteCircle />
//                 {" "}Sub Company
//             </button>


//             <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
//                 <Modal.Header closeButton className="justify-center task-Tab-heading font-weight500    font-size-heading ">
//                     <Modal.Title>Add Sub Company</Modal.Title>
//                 </Modal.Header>
//                 {/* <Modal.Header closeButton>
//                 <Modal.Title>Request Leave</Modal.Title>
//               </Modal.Header> */}
//                 <Modal.Body>
//                     <div className="parent-div">
//                         <div className="bdy-div">
//                             {/* <h6 className="task-Tab-heading   font-weight500    font-size-heading">Birthday </h6> */}
//                             <div className="flex-column">
//                                 <label htmlFor="title" className="form-labels  font-weight500    font-size-subheading" title="Company Name">
//                                     Company Name<span className="required">*</span>
//                                 </label>
//                                 <input
//                                     type="text"
//                                     maxLength={50}
//                                     id="title"
//                                     title="Company Name"
//                                     name="title"
//                                     placeholder="Company Name"
//                                     onChange={handleInputChange}
//                                     value={formData.title}
//                                     className={`form-input ${errors.title ? "error" : inputState.title ? "success" : ""
//                                         }`}
//                                 />
//                                 {errors.title && (
//                                     <span className="error-message font-size-text ">{errors.title}</span>
//                                 )}
//                                 <div className="button-models">
//                                     <button className="model-button   font-weight500   model-button-cancel" type="button" onClick={handleClose}>
//                                         Cancel
//                                     </button>
//                                     <button
//                                         //   type="submit"
//                                         className="model-button   font-weight500    model-button-leave  font-weight500    font-size-heading"
//                                         onClick={handleFormSubmit}
//                                         disabled={loading}
//                                     >
//                                         Submit
//                                     </button>
//                                 </div>
//                                 <p className="error-message font-size-text">
//                                     {isSubmitted && Object.keys(errors).length > 0 && (
//                                         Object.keys(errors).length > 5 ? (
//                                             <h5 className="text-center">Please fill all mandatory fields!</h5>
//                                         ) : (

//                                             <h6 className="text-center">
//                                                 {Object.keys(errors).map((field) =>
//                                                     field.charAt(0).toUpperCase() + field.slice(1)
//                                                 ).join(', ') + ' are required!'}
//                                             </h6>
//                                         )
//                                     )}
//                                 </p>
//                             </div>
//                         </div>
//                     </div>
//                     <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
//                 </Modal.Body>
//             </Modal>
//         </>
//     );
// };

const AddSiteSubcompanys = ({ getSiteSubcompanies }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => {
        setFormData({
            title: "",
        });
        setErrors({});
        setShow(false);
    };
    const handleShow = () => setShow(true);

    const [formData, setFormData] = useState({
        title: "",
    });

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["title"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required!`;
            }
        });
        setErrors(newErrors);
        setIsSubmitted(true);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setInputState({ ...inputState, [name]: value.trim() ? "green" : "" });
        setFormData({ ...formData, [name]: value });
        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (value) delete updatedErrors[name];
            return updatedErrors;
        });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            let res = await axios.post(`${BASE_URL}/siteproject/subcompany/`, formData);
            if (res.status === 200) {
                await getSiteSubcompanies();
                toast.dismiss(loadingToastId);
                toast.success("Sub Company Added Successfully");
                handleClose();
            } else {
                alert(res);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <button title="Add Sub Company" className="btn-animated" onClick={handleShow}>
                <AddwithWhiteCircle /> Sub Company
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Add" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal request-leave" size="md">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <Tag size={22} />
                            </div>
                            <div>
                                <Modal.Title>Add Sub Company</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>Create a new sub company for your organization</p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

                    <Modal.Body className="modern-modal-body">
                        <div className="form-section">
                            <div className="section-title">
                                <Tag size={18} />
                                <h4>Sub Company Details</h4>
                            </div>

                            <div className="form-grid">
                                <div className="form-field full-width">
                                    <label><FileText size={14} /> Company Name<span className="required">*</span></label>
                                    <input
                                        maxLength={50}
                                        type="text"
                                        name="title"
                                        placeholder="Company Name"
                                        onChange={handleInputChange}
                                        value={formData.title}
                                        className={errors.title ? "error" : inputState.title ? "success" : ""}
                                    />
                                    {errors.title && <span className="error-message">{errors.title}</span>}
                                </div>
                            </div>

                            <div className="form-actions">
                                <div></div>
                                <div className="submit-buttons">
                                    <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-primaryy"
                                        onClick={handleFormSubmit}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={18} className="spinning" /> : <Save size={18} />}
                                        <span>{loading ? 'Adding...' : 'Add Sub Company'}</span>
                                    </button>
                                </div>
                            </div>

                            <p className="error-message" style={{ marginTop: '12px' }}>
                                {isSubmitted && Object.keys(errors).length > 0 && (
                                    Object.keys(errors).length > 5 ? (
                                        <span>Please fill all mandatory fields!</span>
                                    ) : (
                                        Object.keys(errors).map((field) => field.charAt(0).toUpperCase() + field.slice(1)).join(', ') + ' are required!'
                                    )
                                )}
                            </p>
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const AddDepartment = ({ getSiteDepartment }) => {

    const [show, setShow] = useState(false);
    const [subcompanyList, setSubCompanyList] = useState([]);

    const handleShow = () => {
        setShow(true);
        getSubCompany();
    }

    const handleClose = () => {
        setFormData({
            title: ""
        })
        setErrors({});
        setShow(false);
    };


    const [formData, setFormData] = useState({
        title: "",
    });

    // !  ************** Validation start **************  ! //

    const getSubCompany = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/siteproject/subcompany/`);
            setSubCompanyList(response.data);
        } catch (error) {
            alert(error);
        }
    }

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["title", "division"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);
        setIsSubmitted(true);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });

        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (value) {
                delete updatedErrors[name];
            }
            return updatedErrors;
        });
    };

    // ?  ************** Validation End **************  ! //
    const [loading, setLoading] = useState(false); //loading logic
    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
            setLoading(true); //loading logic
            try {
                let res = await axios.post(`${BASE_URL}/siteproject/department/`, formData);

                if (res.status === 200) {
                    handleClose();
                    await getSiteDepartment();
                } else {
                    alert(res);
                }
            } catch (err) {
                handleErrorToast(err);
            } finally {
                setLoading(false); //loading logic
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <button title="Add Department" className="upload-svg" onClick={handleShow}>
                <AddwithWhiteCircle />
                {" "}Department
            </button>


            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Header closeButton className="justify-center task-Tab-heading font-weight500    font-size-heading ">
                    <Modal.Title>Add Department</Modal.Title>
                </Modal.Header>
                {/* <Modal.Header closeButton>
                <Modal.Title>Request Leave</Modal.Title>
              </Modal.Header> */}
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            {/* <h6 className="task-Tab-heading   font-weight500    font-size-heading">Birthday </h6> */}
                            <div className="flex-column">
                                <label htmlFor="title" className="form-labels  font-weight500    font-size-subheading" title="Department Name">
                                    Department Name<span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    maxLength={50}
                                    id="title"
                                    title="Department Name"
                                    name="title"
                                    placeholder="Department Name"
                                    onChange={handleInputChange}
                                    value={formData.title}
                                    className={`form-input ${errors.title ? "error" : inputState.title ? "success" : ""
                                        }`}
                                />
                                {errors.title && (
                                    <span className="error-message font-size-text ">{errors.title}</span>
                                )}
                                <div title="Site Sub Company" className="flex-column form-group-selectt">
                                    <label htmlFor="division" className="form-labels  font-weight500    font-size-subheading">
                                        Site Sub Company<span className="required">*</span>
                                    </label>
                                    <br />
                                    <select
                                        id="division"
                                        name="division"
                                        value={formData.division}
                                        onChange={handleInputChange}
                                        className={`form-input form-group-selection ${errors.division ? "error" : inputState.division ? "success" : ""
                                            }`}
                                    >
                                        <option value="">Select a Site Sub Company</option>
                                        {subcompanyList?.sort((a, b) => a.title.localeCompare(b.title, 'en', { ignorePunctuation: true })).map((i, index) => (<>
                                            <option value={i.id}>{i.title}</option>
                                        </>))
                                        }
                                    </select>

                                    <div>
                                        {errors.division && (
                                            <span className="error-message font-size-text ">{errors.division}</span>
                                        )}
                                    </div>
                                </div>



                                <div className="button-models">
                                    <button className="model-button   font-weight500   model-button-cancel" type="button" onClick={handleClose}>
                                        Cancel
                                    </button>
                                    <button
                                        //   type="submit"
                                        className="model-button   font-weight500    model-button-leave  font-weight500    font-size-heading"
                                        onClick={handleFormSubmit}
                                        disabled={loading}
                                    >
                                        Submit
                                    </button>
                                </div>
                                <p className="error-message font-size-text">
                                    {isSubmitted && Object.keys(errors).length > 0 && (
                                        Object.keys(errors).length > 5 ? (
                                            <h5 className="text-center">Please fill all mandatory fields!</h5>
                                        ) : (

                                            <h6 className="text-center">
                                                {Object.keys(errors).map((field) =>
                                                    field.charAt(0).toUpperCase() + field.slice(1)
                                                ).join(', ') + ' are required!'}
                                            </h6>
                                        )
                                    )}
                                </p>
                            </div>

                        </div>
                    </div>
                    <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                </Modal.Body>
            </Modal>
        </>
    );
};

const UpdateSiteSubcompanyupdate = ({ i, getSiteSubcompanies, subCompanyList }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => {
        setFormData(i);
        setShow(true);
    };

    const [filteredList, setFilteredList] = useState([]);

    const [formData, setFormData] = useState({
        title: "",
    });

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["title"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required!`;
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setInputState({ ...inputState, [name]: value.trim() ? "green" : "" });
        setFormData({ ...formData, [name]: value });

        if (name === 'title' && subCompanyList) {
            const filtered = subCompanyList.filter(item =>
                item.title.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredList(filtered);
        }

        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (value) delete updatedErrors[name];
            return updatedErrors;
        });
    };

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            let res = await axios.put(`${BASE_URL}/siteproject/subcompany/${i.id}/`, formData);
            if (res.status === 200) {
                await getSiteSubcompanies();
                toast.dismiss(loadingToastId);
                toast.success("Sub Company Updated Successfully");
                setShow(false);
            } else {
                alert(res);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <button title="Update Sub Company" className="model-delete-button-modern" style={{ backgroundColor: "#00000075" }} onClick={handleShow}>
                <UpdatePencil modernButton="true" />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Update" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal request-leave" size="md">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <Tag size={22} />
                            </div>
                            <div>
                                <Modal.Title>Update Sub Company</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>{i.title}</p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

                    <Modal.Body className="modern-modal-body">
                        <div className="form-section">
                            <div className="section-title">
                                <Tag size={18} />
                                <h4>Sub Company Details</h4>
                            </div>

                            <div className="form-grid">
                                <div className="form-field full-width">
                                    <label><FileText size={14} /> Title<span className="required">*</span></label>
                                    <input
                                        maxLength={50}
                                        type="text"
                                        name="title"
                                        placeholder="Sub Company Name"
                                        onChange={handleInputChange}
                                        value={formData.title}
                                        className={errors.title ? "error" : inputState.title ? "success" : ""}
                                    />
                                    {errors.title && <span className="error-message">{errors.title}</span>}
                                    {filteredList.length > 0 && formData.title !== i.title && (
                                        <div className="list-group" style={{ marginTop: '6px' }}>
                                            {filteredList.map((item, index) => (
                                                <div key={index} className="list-group-item">
                                                    {item.title}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="form-actions">
                                <div></div>
                                <div className="submit-buttons">
                                    <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-primaryy"
                                        onClick={handleFormSubmitPut}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={18} className="spinning" /> : <Save size={18} />}
                                        <span>{loading ? 'Updating...' : 'Update'}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const UpdateDepartment = ({ i, getSiteDepartment }) => {
    const [show, setShow] = useState(false);
    const [subcompanyList, setSubCompanyList] = useState([]);

    const handleClose = () => {
        setShow(false);
        setFormData(i);
    }
    const handleShow = () => {
        setFormData(i);
        setShow(true);
        getSubCompany();
    };

    const [formData, setFormData] = useState({
        title: "",
    });


    // !  ************** Validation start **************  ! //

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = ["title"];
        requiredFields.forEach((field) => {
            if (!formData[field]) {
                newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
                    } is required !`;
            }
        });

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };
    const getSubCompany = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/siteproject/subcompany/`);
            setSubCompanyList(response.data);
        } catch (error) {
            alert(error);
        }
    }


    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };

    // ?  ************** Validation End **************  ! //

    const handleFormSubmitPut = async (e) => {
        e.preventDefault();

        if (validateForm()) {
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
            try {
                let res = await axios.put(
                    `${BASE_URL}/siteproject/department/${i.id}/`,
                    formData
                );

                if (res.status === 200) {
                    handleClose();
                    await getSiteDepartment();
                } else {
                    alert(res);
                }
            } catch (err) {
                //toast Logic
                handleErrorToast(err)
            } finally {
                toast.dismiss(loadingToastId);
            }
        }
    };

    return (
        <>
            <button title='Update' className="model-edit-button" onClick={handleShow}>
                <UpdatePencil />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Header closeButton>
                    <Modal.Title>Update SiteDepartment {i.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="parent-div">
                        <div className="bdy-div">
                            {/* <h6 className="task-Tab-heading   font-weight500    font-size-heading">Birthday </h6> */}
                            <div className="flex-column">
                                <label htmlFor="title" className="form-labels  font-weight500    font-size-subheading">
                                    Department Name<span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    maxLength={50}
                                    id="title"
                                    name="title"
                                    placeholder="Department Name"
                                    onChange={handleInputChange}
                                    value={formData.title}
                                    className={`form-input ${errors.title ? "error" : inputState.tile ? "success" : ""
                                        }`}
                                />
                                {errors.title && (
                                    <span className="error-message font-size-text ">{errors.title}</span>
                                )}
                            </div>

                            <div title="Site Sub Company" className="flex-column form-group-selectt">
                                <label htmlFor="division" className="form-labels  font-weight500    font-size-subheading">
                                    Site Sub Company
                                </label>
                                <br />
                                <select
                                    id="division"
                                    name="division"
                                    value={formData.division}
                                    onChange={handleInputChange}
                                    className={`form-input form-group-selection ${errors.division ? "error" : inputState.division ? "success" : ""
                                        }`}
                                >
                                    <option value="">Select a Site SubCompany</option>
                                    {subcompanyList?.sort((a, b) => a.title.localeCompare(b.title, 'en', { ignorePunctuation: true }))?.map((i, index) => (<>
                                        <option value={i.id}>{i.title}</option>
                                    </>))
                                    }
                                </select>

                                <div>
                                    {errors.division && (
                                        <span className="error-message font-size-text ">{errors.division}</span>
                                    )}
                                </div>
                            </div>

                            <div className="button-models">
                                <button className="model-button   font-weight500   model-button-cancel " onClick={handleClose}>
                                    Cancel
                                </button>
                                <button
                                    onClick={handleFormSubmitPut}
                                    className="model-button   font-weight500    model-button-leave  font-weight500    font-size-heading"
                                >
                                    Update
                                </button>
                            </div>
                        </div>
                    </div>
                    <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                </Modal.Body>
            </Modal>
        </>
    );
};

const DeleteDepartment = ({ i, getSiteDepartment }) => {
    const [show, setShow] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleFormSubmitDelete = async (e) => {
        e.preventDefault();

        const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic
        try {
            let res = await axios.delete(`${BASE_URL}/siteproject/department/${i.id}/`);

            if (res.status === 200) {
                setShow(false);
                await getSiteDepartment();
            } else {
                alert(res);
            }
        } catch (err) {
            //toast Logic
            handleErrorToast(err)
        } finally {
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <button title='Delete' className="model-delete-button" onClick={handleShow}>
                <DeleteDustbin />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="request-leave width-40vw">
                <Modal.Header closeButton>
                    <Modal.Title>Delete SiteDepartment {i.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="parent-div">
                        Are You Sure You Want to delete SiteDepartment {i.title}
                        <div className="button-models">
                            <button className="model-button   font-weight500    model-button-cancel" onClick={handleClose}>
                                Cancel
                            </button>
                            <button
                                onClick={handleFormSubmitDelete}
                                className="model-button   font-weight500    model-button-delete"
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                    <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                </Modal.Body>
            </Modal>
        </>
    );
};

const DeleteSiteSubcompany = ({ i, getSiteSubcompanies }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const hasEmployees = i.employee_count > 0;

    const handleFormSubmitDelete = async (e) => {
        e.preventDefault();
        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            let res = await axios.delete(`${BASE_URL}/siteproject/subcompany/${i.id}/`);
            if (res.status === 200) {
                await getSiteSubcompanies();
                toast.dismiss(loadingToastId);
                toast.success("Deleted Successfully");
                setShow(false);
            } else {
                alert(res);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <button title="Delete Sub Company" className="model-delete-button-modern" style={{ backgroundColor: "#00000075" }} onClick={handleShow}>
                <DeleteDustbin modernButton="true" />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Delete" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-required-modal" centered>
                    <Modal.Header className="modern-modal-header requirement-modal-header">
                        <div className="header-content">
                            <div className="requirement-header-icon">
                                {hasEmployees ? <AlertTriangle size={22} /> : <Trash2 size={22} />}
                            </div>
                            <div>
                                <Modal.Title>{hasEmployees ? "Cannot Delete" : "Delete Sub Company"}</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    {hasEmployees ? "Employees are still assigned" : "This action cannot be undone"}
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body requirement-modal-body">
                        <div className="requirement-mode-summary" style={{ marginBottom: '20px' }}>
                            <AlertCircle size={16} />
                            {hasEmployees ? (
                                <span>
                                    <strong>{i.employee_count}</strong> employee{i.employee_count !== 1 ? 's are' : ' is'} assigned to <strong>{i.title}</strong>. Please reassign {i.employee_count !== 1 ? 'them' : 'them'} to a different sub company before deleting.
                                </span>
                            ) : (
                                <span>
                                    Are you sure you want to delete <strong>{i.title}</strong>?
                                </span>
                            )}
                        </div>

                        <div className="submit-buttons" style={{ justifyContent: 'flex-end' }}>
                            <button type="button" className="btn-secondaryy" onClick={handleClose} disabled={loading}>
                                {hasEmployees ? "Close" : "Cancel"}
                            </button>
                            {!hasEmployees && (
                                <button
                                    type="button"
                                    className="btn-primaryy"
                                    style={{ background: "#dc3545", borderColor: "#dc3545" }}
                                    onClick={handleFormSubmitDelete}
                                    disabled={loading}
                                >
                                    {loading ? <Loader size={18} className="spinning" /> : <Trash2 size={18} />}
                                    <span>{loading ? 'Deleting...' : 'Delete'}</span>
                                </button>
                            )}
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const UploadSiteEmployee = ({ id, getSiteEmployee, site_prcode, designation, division, ROLE }) => {

    const [show, setShow] = useState(false);
    const [fileName, setFileName] = useState("");
    const [fileSize, setFileSize] = useState("");
    const [excelJson, setExcelJson] = useState(null);
    const [docErrors, setDocErrors] = useState({});
    const [docInputState, setDocInputState] = useState({});
    const [isDragging, setIsDragging] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const fileInputRef = useRef(null);

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    const handleDownload = () => {
        const headers = ["emp_code", site_prcode ? null : "project", "name", "emp_role", "dob", "gender", "married_status", "qualification", "designation", "professional_type", "current_address", "mobile", "email", "joining_date", "employee_status", "emergency_person_name", "emergency_phone_number", "emergency_relation", "emergency_address", "division_name", "actual_salary", "boq_salary", "account_number", "ifsc_code", "bank_name", "branch_address", "uan", "esi", "aadhaar", "pan"].filter(Boolean);

        const data = [
            headers,
            [],
            ["REFERENCES:"],
            [],
            ["GENERAL INSTRUCTIONS:"],
            ["   • Employee code should be unique."],
            ["   • Email should be valid."],
            ["   • Project should be existing."],
            ["   • In Project field only fill site project code."],
            ["   • Aadhaar should have 12 digits with no spaces."],
            ["   • PAN should have 10 digits with no spaces."],
            ["   • Mobile numbers should have 10 digits, no +91."],
            ["   • Add only one mobile number."],
            [],
            ["EMPLOYEE ROLE OPTIONS:"],
            ...(Array.isArray(ROLE) && ROLE.length > 0
                ? ROLE.map((r) => [`   • ${r.role_name}`])
                : []
            ),
            [],
            ["PROFESSIONAL TYPE OPTIONS:"],
            ["   • Key Professional"],
            ["   • Sub Professional"],
            ["   • Support Staff"],
            [],
            ["Gender  OPTIONS:"],
            ["   • male"],
            ["   • female"],
            ["   • other"],
            [],
            ["EMPLOYEE STATUS OPTIONS:"],
            ["   • active"],
            ["   • inactive"],
            [],
            ["MARITAL STATUS OPTIONS:"],
            ["   • Single"],
            ["   • Married"],
            ["   • Divorced"],
            ["   • Widowed"],
            ["   • Separated"],
            [],
            [" Division OPTIONS:"],
            ...(Array.isArray(division) && division.length > 0
                ? division.map((r) => [`   • ${r.name}`])
                : []
            ),
            [],
            ["NOTE: All Dates should be in DD-MM-YYYY format."]
        ];

        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Employee Format");

        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "binary" });

        const blob = new Blob([s2ab(wbout)], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "Employee_Upload_Format.xlsx";
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const s2ab = (s) => {
        const buf = new ArrayBuffer(s.length);
        const view = new Uint8Array(buf);
        for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
        return buf;
    };

    const handleClose = () => {
        setFileName("");
        setFileSize("");
        setExcelJson(null);
        setDocErrors({});
        setDocInputState({});
        setShow(false);
    };

    const handleShow = () => setShow(true);

    const handleRemoveFile = () => {
        setFileName("");
        setFileSize("");
        setExcelJson(null);
        setDocErrors({});
        setDocInputState({});
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const processFile = (file) => {
        if (!file) return;
        const validFileTypes = ["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "application/vnd.ms-excel"];
        if (!validFileTypes.includes(file.type)) {
            setDocErrors({ document_file: "Please upload a valid Excel file (.xlsx or .xls)." });
            setFileName("");
            setFileSize("");
            return;
        }

        setFileName(file.name);
        setFileSize(formatFileSize(file.size));
        setDocErrors({});
        setDocInputState({ document_file: "green" });
        readExcel(file);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        processFile(file);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        processFile(file);
    };

    const readExcel = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });

            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

            const headerIndex = jsonData.findIndex(row => row.includes("emp_code"));

            if (headerIndex === -1) {
                toast.error("Header row not found in Excel");
                return;
            }

            const slicedData = jsonData.slice(headerIndex);

            const filteredRows = slicedData.filter(row => {
                const firstCell = row[0]?.toString().trim() || "";
                return (
                    row.length > 0 &&
                    firstCell !== "REFERENCES:" &&
                    !firstCell.includes("GENERAL INSTRUCTIONS") &&
                    !firstCell.includes("EMPLOYEE ROLE OPTIONS") &&
                    !firstCell.includes("PROFESSIONAL TYPE OPTIONS") &&
                    !firstCell.includes("EMPLOYEE STATUS OPTIONS") &&
                    !firstCell.includes("MARITAL STATUS OPTIONS") &&
                    !firstCell.includes("NOTE") &&
                    !/^[•\-*]/.test(firstCell)
                );
            });

            const [headers, ...dataRows] = filteredRows;
            const finalData = dataRows.map(row =>
                Object.fromEntries(headers.map((key, i) => [key, row[i] ?? ""]))
            );

            const convertToYYYYMMDD = (input) => {
                try {
                    let date;
                    if (typeof input === "number") {
                        const excelEpoch = new Date(1900, 0, 1);
                        const daysOffset = input - 1;
                        date = new Date(excelEpoch.getTime() + daysOffset * 86400000);
                    } else if (/^\d{4}\.\d{2}\.\d{2}$/.test(input)) {
                        const [year, month, day] = input.split('.');
                        date = new Date(`${year}-${month}-${day}`);
                    } else if (/^\d{2}\.\d{2}\.\d{4}$/.test(input)) {
                        const [day, month, year] = input.split('.');
                        date = new Date(`${year}-${month}-${day}`);
                    } else {
                        date = new Date(input);
                    }

                    if (isNaN(date.getTime())) {
                        throw new Error('Invalid date format');
                    }

                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}-${month}-${day}`;
                } catch {
                    return null;
                }
            };

            const formattedJson = finalData.map((row) => {
                const obj = {};
                headers.forEach((header) => {
                    if (["dob", "joining_date"].includes(header) && row[header]) {
                        obj[header] = convertToYYYYMMDD(row[header]);
                    } else {
                        obj[header] = row[header] !== undefined ? row[header] : null;
                    }
                });

                if (site_prcode) {
                    obj["project"] = site_prcode;
                }

                obj["sub_company"] = "Saptagon";
                return obj;
            });

            formattedJson.forEach((item, index) => {
                if (typeof item !== 'object' || item === null) {
                    toast.error(`Row ${index + 2} in Excel is invalid.`);
                    return;
                }

                const matched = division.find((d) =>
                    d.name?.toLowerCase() === item.division_name?.toLowerCase());

                if (matched) {
                    item.division = matched.id;
                } else {
                    toast.error(`Division not found for: ${item.division_name || 'Missing'} in row ${index + 2}.`);
                }
            });

            setExcelJson(formattedJson);
        };

        reader.readAsArrayBuffer(file);
    };

    const handleUpload = async () => {
        if (excelJson) {
            setShowProgressModal(true);
            setUploadProgress(0);

            const loadingToastId = toast.loading("Uploading: Please wait...");

            let progress = 0;
            const interval = setInterval(() => {
                progress += 10;
                if (progress >= 100) {
                    progress = 100;
                    clearInterval(interval);
                }
                setUploadProgress(progress);
            }, 100);

            try {
                const res = await axios.post(`${BASE_URL}/siteproject/employeebulk/`, excelJson);
                if (res.status === 200) {
                    await getSiteEmployee();
                    toast.dismiss(loadingToastId);
                    toast.success("Uploaded Successfully");
                    handleClose();
                }
            } catch (err) {
                clearInterval(interval);
                setUploadProgress(0);
                handleError(err, loadingToastId);
            } finally {
                clearInterval(interval);
                setTimeout(() => {
                    setShowProgressModal(false);
                }, 1500);
                toast.dismiss(loadingToastId);
            }
        } else {
            setDocErrors({ document_file: "Please upload a valid Excel file." });
        }
    };

    const handleError = (err, loadingToastId) => {
        toast.dismiss(loadingToastId);
        handleErrorToast(err);
    };

    return (
        <>
            <style>{`
                .ub-upload-zone {
                    border: 2px dashed #c7cdd6;
                    border-radius: 14px;
                    background: #fafbfd;
                    padding: 32px 20px;
                    text-align: center;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    position: relative;
                }
                .ub-upload-zone:hover {
                    border-color: #6366f1;
                    background: #f5f5ff;
                }
                .ub-upload-zone.dragging {
                    border-color: #6366f1;
                    background: #eef0ff;
                    transform: scale(1.01);
                }
                .ub-upload-zone.has-error {
                    border-color: #e11d48;
                    background: #fff1f2;
                }
                .ub-upload-icon-circle {
                    width: 56px;
                    height: 56px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #6366f1, #8b5cf6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 14px;
                    color: white;
                    box-shadow: 0 4px 14px rgba(99, 102, 241, 0.3);
                }
                .ub-upload-title {
                    font-size: 15px;
                    font-weight: 600;
                    color: #1f2937;
                    margin: 0 0 4px;
                }
                .ub-upload-subtitle {
                    font-size: 13px;
                    color: #6b7280;
                    margin: 0;
                }
                .ub-upload-browse {
                    color: #6366f1;
                    font-weight: 600;
                    text-decoration: underline;
                }
                .ub-file-card {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    background: #f0fdf4;
                    border: 1px solid #bbf7d0;
                    border-radius: 12px;
                    padding: 14px 16px;
                    margin-top: 12px;
                    animation: ub-fade-in 0.25s ease;
                }
                .ub-file-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    background: #16a34a;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    flex-shrink: 0;
                }
                .ub-file-info {
                    flex: 1;
                    min-width: 0;
                }
                .ub-file-name {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1f2937;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .ub-file-size {
                    font-size: 12px;
                    color: #6b7280;
                }
                .ub-file-remove {
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: #9ca3af;
                    padding: 6px;
                    border-radius: 8px;
                    display: flex;
                    transition: all 0.15s ease;
                    flex-shrink: 0;
                }
                .ub-file-remove:hover {
                    background: #fee2e2;
                    color: #dc2626;
                }
                @keyframes ub-fade-in {
                    from { opacity: 0; transform: translateY(-4px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .ub-preview-wrap {
                    border: 1px solid #e5e7eb;
                    border-radius: 14px;
                    overflow: hidden;
                    margin-top: 8px;
                }
                .ub-preview-scroll {
                    overflow-x: auto;
                    max-height: 340px;
                    overflow-y: auto;
                }
                .ub-preview-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 13px;
                    white-space: nowrap;
                }
                .ub-preview-table thead th {
                    position: sticky;
                    top: 0;
                    background: #f8fafc;
                    color: #475569;
                    font-weight: 600;
                    text-align: left;
                    padding: 10px 14px;
                    border-bottom: 2px solid #e2e8f0;
                    z-index: 1;
                }
                .ub-preview-table tbody td {
                    padding: 9px 14px;
                    color: #334155;
                    border-bottom: 1px solid #f1f5f9;
                }
                .ub-preview-table tbody tr:nth-child(even) {
                    background: #fbfcfe;
                }
                .ub-preview-table tbody tr:hover {
                    background: #f0f4ff;
                }
                .ub-preview-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 10px 4px;
                }
                .ub-preview-badge {
                    background: #eef2ff;
                    color: #4338ca;
                    font-size: 12px;
                    font-weight: 600;
                    padding: 4px 10px;
                    border-radius: 999px;
                }
                .ub-download-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    background: #f8fafc;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    padding: 14px 16px;
                    margin-bottom: 18px;
                    gap: 12px;
                }
                .ub-download-row p {
                    margin: 0;
                    font-size: 13.5px;
                    color: #475569;
                }
            `}</style>

            <button title="Upload" className="btn-animated" onClick={handleShow} style={{ margin: "2px" }}>
                <UploadCloud size={16} style={{ marginRight: "6px" }} /> Bulk Upload
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Upload" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal model-width80vw" size="xl">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <Upload size={24} />
                            </div>
                            <div>
                                <Modal.Title>Upload Bulk Employee Data</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>Upload multiple employees at once using the Excel format</p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body">

                        <div className="ub-download-row">
                            <p><FileText size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                Download the Excel template, fill in employee details, then upload it below.
                            </p>
                            <button onClick={handleDownload} className="btn-primaryy" type="button" style={{ flexShrink: 0 }}>
                                <Download size={16} />
                                <span>Format</span>
                            </button>
                        </div>

                        <div className="section-title">
                            <Upload size={18} />
                            <h4>Upload File</h4>
                        </div>

                        <div
                            className={`ub-upload-zone ${isDragging ? 'dragging' : ''} ${docErrors.document_file ? 'has-error' : ''}`}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <div className="ub-upload-icon-circle">
                                <Upload size={24} />
                            </div>
                            <p className="ub-upload-title">
                                Drag & drop your Excel file here
                            </p>
                            <p className="ub-upload-subtitle">
                                or <span className="ub-upload-browse">browse</span> to choose a file &nbsp;·&nbsp; .xlsx or .xls
                            </p>
                            <input
                                ref={fileInputRef}
                                id="document_file"
                                name="document_file"
                                type="file"
                                accept=".xlsx, .xls"
                                onChange={handleFileChange}
                                style={{ display: "none" }}
                            />
                        </div>

                        {docErrors.document_file && (
                            <div className="tab-error-message" style={{ marginTop: '10px' }}>
                                <AlertCircle size={14} />
                                <span>{docErrors.document_file}</span>
                            </div>
                        )}

                        {fileName && !docErrors.document_file && (
                            <div className="ub-file-card">
                                <div className="ub-file-icon">
                                    <FileText size={20} />
                                </div>
                                <div className="ub-file-info">
                                    <div className="ub-file-name">{fileName}</div>
                                    <div className="ub-file-size">{fileSize}{excelJson ? ` · ${excelJson.length} records found` : ''}</div>
                                </div>
                                <button type="button" className="ub-file-remove" onClick={handleRemoveFile} title="Remove file">
                                    <X size={18} />
                                </button>
                            </div>
                        )}

                        {excelJson && excelJson.length > 0 && (
                            <>
                                <div className="ub-preview-header">
                                    <div className="section-title" style={{ marginBottom: 0 }}>
                                        <Users size={18} />
                                        <h4 style={{ margin: 0 }}>Preview</h4>
                                    </div>
                                    <span className="ub-preview-badge">{excelJson.length} employees</span>
                                </div>

                                <div className="ub-preview-wrap">
                                    <div className="ub-preview-scroll  table-box scroll-container-table">
                                        <table className="ub-preview-table">
                                            <thead className="table-heading">
                                                <tr>
                                                    <th>Emp Code</th>
                                                    {site_prcode ? null : <th>Project</th>}
                                                    <th>Name</th>
                                                    <th>Role</th>
                                                    <th>DOB</th>
                                                    <th>Gender</th>
                                                    <th>Marital Status</th>
                                                    <th>Qualification</th>
                                                    <th>Designation</th>
                                                    <th>Type</th>
                                                    <th>Address</th>
                                                    <th>Mobile</th>
                                                    <th>Email</th>
                                                    <th>Joining Date</th>
                                                    <th>Status</th>
                                                    <th>Emergency Name</th>
                                                    <th>Emergency Phone</th>
                                                    <th>Relation</th>
                                                    <th>Emergency Address</th>
                                                    <th>Division</th>
                                                    <th>Actual Salary</th>
                                                    <th>BOQ Salary</th>
                                                    <th>Account No.</th>
                                                    <th>IFSC</th>
                                                    <th>Bank</th>
                                                    <th>Branch</th>
                                                    <th>UAN</th>
                                                    <th>ESI</th>
                                                    <th>Aadhaar</th>
                                                    <th>PAN</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {excelJson.map((employee, index) => (
                                                    <tr key={index}>
                                                        <td>{employee.emp_code}</td>
                                                        {site_prcode ? null : <td>{employee.project}</td>}
                                                        <td>{employee.name}</td>
                                                        <td>{employee.emp_role}</td>
                                                        <td>{employee.dob}</td>
                                                        <td>{employee.gender}</td>
                                                        <td>{employee.married_status}</td>
                                                        <td>{employee.qualification}</td>
                                                        <td>{employee.designation}</td>
                                                        <td>{employee.professional_type}</td>
                                                        <td>{employee.current_address}</td>
                                                        <td>{employee.mobile}</td>
                                                        <td>{employee.email}</td>
                                                        <td>{employee.joining_date}</td>
                                                        <td>{employee.employee_status}</td>
                                                        <td>{employee.emergency_person_name}</td>
                                                        <td>{employee.emergency_phone_number}</td>
                                                        <td>{employee.emergency_relation}</td>
                                                        <td>{employee.emergency_address}</td>
                                                        <td>{employee.division_name}</td>
                                                        <td>{employee.actual_salary}</td>
                                                        <td>{employee.boq_salary}</td>
                                                        <td>{employee.account_number}</td>
                                                        <td>{employee.ifsc_code}</td>
                                                        <td>{employee.bank_name}</td>
                                                        <td>{employee.branch_address}</td>
                                                        <td>{employee.uan}</td>
                                                        <td>{employee.esi}</td>
                                                        <td>{employee.aadhaar}</td>
                                                        <td>{employee.pan}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="form-actions">
                            <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                Cancel
                            </button>
                            <button type="button" className="btn-primaryy" onClick={handleUpload} disabled={!excelJson}>
                                <Upload size={18} />
                                <span>Upload</span>
                            </button>
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};


const AddSiteEmployee = ({ projectId, togglecompany, roleoption, getSiteEmployee, siteProjectList, siteSubCompanyList, companyId }) => {

    const [designationList, setDesignationList] = useState([]);
    const siteSubcompany = sessionStorage.getItem("sitemanagement_project_sub_company");
    const role = sessionStorage.getItem('sitemanagement_role');
    const [show, setShow] = useState(false);
    const [roleList, setRoleList] = useState([]);
    const siteRole = sessionStorage.getItem("sitemanagement_role");
    const [activeSection, setActiveSection] = useState('employee');
    const [tabValidationAttempted, setTabValidationAttempted] = useState({});
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const sections = [
        { id: 'employee', label: 'Employee Details', icon: Briefcase },
        { id: 'personal', label: 'Personal Details', icon: User },
        { id: 'bank', label: 'Bank Details', icon: CreditCard },
    ];

    const handleClose = () => {
        setShow(false);
        setActiveSection('employee');
        setTabValidationAttempted({});
        setFormData({
            emp_role: "null",
            emp_code: "",
            name: "",
            email: "",
            mobile: "",
            designation: "",
            professional_type: "",
            current_address: "",
            joining_date: null,
            employee_status: "active",
            account_holder_name: "",
            sub_company: companyId ? companyId : "",
            division: "",
            project: projectId !== null ? projectId : null,
            dob: "",
            employment_type: "",
            gender: "",
            married_status: "",
            qualification: "",
            emergency_person_name: "",
            emergency_phone_number: "",
            emergency_relation: "",
            emergency_address: "",
            account_number: "",
            ifsc_code: "",
            bank_name: "",
            branch_address: "",
            aadhaar: "",
            emp_department: "",
            uan: "",
            inactive_date: null,
        });
        setErrors({});
    };

    const departmentOptions = [
        { label: "Accounts", value: "Accounts" },
        { label: "Administration", value: "Administration" },
        { label: "Auxiliary", value: "Auxiliary" },
        { label: "BD", value: "BD" },
        { label: "Contracts", value: "Contracts" },
        { label: "Environmental Engineer", value: "Environmental Engineer" },
        { label: "Equipment", value: "Equipment" },
        { label: "Highway", value: "Highway" },
        { label: "Humane Resource", value: "Humane Resource" },
        { label: "Intermittent", value: "Intermittent" },
        { label: "IT", value: "IT" },
        { label: "Lab", value: "Lab" },
        { label: "Management", value: "Management" },
        { label: "Project Co-ordination", value: "Project Co-ordination" },
        { label: "Quantity Survey", value: "Quantity Survey" },
        { label: "Road Safety", value: "Road Safety" },
        { label: "Structure", value: "Structure" },
        { label: "Support Staff", value: "Support Staff" },
    ];

    const [requireFields, setRequiredFields] = useState(false);
    const [showrequired, setShowRequired] = useState(false);

    const handleHideRequiredFieldModal = () => setShowRequired(false);
    const handleShow = () => setShow(true);


    const [formData, setFormData] = useState({
        emp_code: "",
        emp_role: null,
        name: "",
        email: "",
        mobile: "",
        designation: "",
        professional_type: "",
        current_address: "",
        account_holder_name: "",
        joining_date: null,
        employee_status: "active",
        sub_company: companyId ? companyId : "",
        division: "",
        multiple_project: [],
        employment_type: "",
        emp_department: "",
        project: projectId !== "null" ? projectId : null,
        dob: null,
        gender: "",
        married_status: "",
        qualification: "",
        emergency_person_name: "",
        emergency_phone_number: "",
        emergency_relation: "",
        emergency_address: "",
        account_number: "",
        ifsc_code: "",
        bank_name: "",
        branch_address: "",
        aadhaar: "",
        uan: "",
        pan: "",
        replacement_deduction: "",
        inactive_date: null,
    });

    useEffect(() => {
        if (formData.division && (companyId !== "null" || companyId !== null)) {
            const filterSubCompany = siteSubCompanyList.filter(i => i.id === formData.division);
            setFormData((prev) => ({
                ...prev,
                sub_company: filterSubCompany[0].sub_company
            }));
        }
    }, [formData.division, companyId]);

    useEffect(() => {
        if (formData.division != null && formData.division !== undefined && formData.division !== "" && show === true) {
            const getDesignation = async () => {
                try {
                    const response = await axios.get(`${BASE_URL}/siteproject/designationfilterbysubcompanycount/${formData.division}/null/${siteSubcompany}/`);
                    setDesignationList(response.data);
                } catch (error) {
                    console.error("Error fetching designations:", error);
                }
            };
            getDesignation();
        }
    }, [formData.division, show, siteSubcompany]);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            employee_status: (prev.inactive_date !== null && prev.inactive_date !== "") ? "inactive" : "active"
        }));
    }, [formData.inactive_date]);

    useEffect(() => {
        if (formData.employee_status === "active") {
            setFormData((prev) => ({ ...prev, inactive_date: "" }));
        }
    }, [formData.employee_status]);

    useEffect(() => {
        if (formData.inactive_date === "") {
            setFormData((prev) => ({ ...prev, inactive_date: null }));
        }
    }, [formData.inactive_date]);

    useEffect(() => {
        if (formData.employment_type === "temporary") {
            setFormData((prev) => ({ ...prev, project: null }));
        } else if (formData.employment_type === "permanent") {
            setFormData((prev) => ({ ...prev, multiple_project: [] }));
        }
    }, [formData.employment_type]);

    useEffect(() => {
        setFormData((prev) => ({ ...prev, project: null, multiple_project: [] }));
    }, [formData.emp_role]);

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    // ---------- Field labels for nicer messages ----------
    const fieldLabels = {
        emp_code: 'Emp Code',
        emp_role: 'Employee Role',
        professional_type: 'Employee Type',
        employment_type: 'Employment Type',
        emp_department: 'Department',
        joining_date: 'Joining Date',
        designation: 'Designation',
        name: 'Name',
        email: 'Email',
        mobile: 'Mobile No.',
        gender: 'Gender',
        married_status: 'Marital Status',
        aadhaar: 'Aadhaar Card No.',
        pan: 'PAN No.',
        dob: 'Date of Birth',
        account_number: 'Account Number',
        ifsc_code: 'IFSC Code',
        account_holder_name: 'Account Holder Name',
        bank_name: 'Bank Name',
        project: 'Site Project',
        multiple_project: 'Site Projects',
    };

    // ---------- Tab-wise required fields (mirrors validateForm logic) ----------
    const getTabRequiredFields = (tabId, data = formData) => {
        let fields = [];

        if (requireFields) {
            const map = {
                employee: ['emp_code', 'emp_role', 'professional_type', 'employment_type', 'emp_department', 'joining_date', 'designation'],
                personal: ['name', 'email', 'mobile', 'gender', 'married_status', 'aadhaar', 'pan'],
                bank: ['account_number', 'ifsc_code', 'account_holder_name', 'bank_name'],
            };
            fields = map[tabId] || [];
        } else {
            const map = {
                employee: ['emp_code', 'emp_role'],
                personal: ['name', 'email', 'aadhaar', 'mobile', 'dob'],
                bank: [],
            };
            fields = map[tabId] || [];

            if (tabId === 'employee') {
                if (data.employment_type === "temporary" && data.emp_role === "SiteManagement") {
                    fields = [...fields, 'multiple_project'];
                }
                if (data.employment_type === "permanent" && data.emp_role === "SiteManagement") {
                    fields = [...fields, 'project'];
                }
            }
        }

        return fields;
    };

    const isFieldEmpty = (field, data = formData) => {
        const value = data[field];
        if (Array.isArray(value)) return value.length === 0;
        return value === null || value === undefined || value === "null" || (typeof value === 'string' && value.trim() === "");
    };

    // Does the given tab currently have any unfilled required field (live check)
    const tabHasMissingFields = (tabId) => getTabRequiredFields(tabId).some((field) => isFieldEmpty(field));

    // Validates the current tab, sets field-level errors (used when user attempts to leave a tab)
    const validateCurrentTab = () => {
        const currentTab = activeSection;
        const fieldsToValidate = getTabRequiredFields(currentTab);
        const newErrors = {};

        fieldsToValidate.forEach((field) => {
            if (isFieldEmpty(field)) {
                newErrors[field] = `${fieldLabels[field] || field} is required!`;
            }
        });

        setErrors(prev => {
            const updated = { ...prev, ...newErrors };
            fieldsToValidate.forEach((field) => {
                if (!isFieldEmpty(field)) delete updated[field];
            });
            return updated;
        });

        setTabValidationAttempted(prev => ({ ...prev, [currentTab]: true }));

        return Object.keys(newErrors).length === 0;
    };

    const hasCurrentTabErrors = () => getTabRequiredFields(activeSection).some(field => errors[field]);

    const getCurrentTabErrorMessage = () => {
        const missingFields = getTabRequiredFields(activeSection)
            .filter(field => isFieldEmpty(field))
            .map(field => fieldLabels[field] || field);

        if (missingFields.length === 0) return null;
        if (missingFields.length === 1) return `${missingFields[0]} is required to proceed`;

        const tabLabel = sections.find(s => s.id === activeSection)?.label || activeSection;
        return `Please fill all required fields in ${tabLabel} before proceeding`;
    };

    const validateForm = () => {
        let requiredFields = [];
        if (requireFields) {
            requiredFields = [
                'emp_code', 'name', 'employment_type', 'email', 'mobile', 'designation',
                'professional_type', 'joining_date', 'gender', 'emp_department', 'married_status',
                'account_number', 'account_holder_name', 'ifsc_code', 'bank_name', 'aadhaar', 'pan', 'emp_role',
            ].filter(Boolean);
        } else {
            requiredFields = ['emp_code', 'name', 'emp_role', 'email', 'aadhaar', 'mobile', 'dob'].filter(Boolean);
        }

        if (siteRole === "SiteManagement") {
            requiredFields = requiredFields.filter(field => field !== 'division');
        }

        const newErrors = requiredFields.reduce((acc, field) => {
            if (!formData[field] || formData[field] === "null" || (Array.isArray(formData[field]) && formData[field].length === 0)) {
                acc[field] = `${fieldLabels[field] || field} is required!`;
            }
            return acc;
        }, {});

        if (!requireFields) {
            if (formData.employment_type === "temporary" && formData.emp_role === "SiteManagement" && (!Array.isArray(formData.multiple_project) || formData.multiple_project.length === 0)) {
                newErrors["multiple_project"] = "Projects are required!";
            }
            if (formData.employment_type === "permanent" && formData.emp_role === "SiteManagement" && (!formData.project || formData.project.trim() === "")) {
                newErrors["project"] = "Project is required!";
            }
        }

        setErrors(newErrors);
        setIsSubmitted(true);
        return Object.keys(newErrors).length === 0;
    };
    const handleShowRequiredFieldModal = () => {
        if (role === "Admin") {
            setShowRequired(true);
        } else {
            setShow(true);
            setRequiredFields(true);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === "emergency_phone_number") {
            if (!/^\d*$/.test(value)) return;
            if (value.length > 10) return;
            if (value.length < 10) {
                setErrors((prev) => ({ ...prev, emergency_phone_number: "emergency_phone_number must be 10 digits" }));
            } else {
                setErrors((prev) => { const n = { ...prev }; delete n.emergency_phone_number; return n; });
            }
        }
        if (name === "mobile") {
            if (!/^\d*$/.test(value)) return;
            if (value.length > 10) return;
            if (value.length < 10) {
                setErrors((prev) => ({ ...prev, mobile: "Mobile number must be 10 digits" }));
            } else {
                setErrors((prev) => { const n = { ...prev }; delete n.mobile; return n; });
            }
        }
        if (name === "pan") {
            const pan = value.toUpperCase();
            if (!/^[A-Z0-9]*$/.test(pan)) return;
            if (pan.length === 10 && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(pan)) {
                setErrors((prev) => ({ ...prev, pan: "Invalid PAN format (ABCDE1234F)" }));
            } else {
                setErrors((prev) => { const n = { ...prev }; delete n.pan; return n; });
            }
            setFormData((prev) => ({ ...prev, pan: pan }));
            return;
        }
        if (name === "aadhaar") {
            if (!/^\d*$/.test(value)) return;
            if (value.length > 12) return;
            if (value.length < 12) {
                setErrors((prev) => ({ ...prev, aadhaar: "aadhaar number must be 12 digits" }));
            } else {
                setErrors((prev) => { const n = { ...prev }; delete n.aadhaar; return n; });
            }
        }
        if (name === "joining_date" && value) {
            const selectedDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (selectedDate > today) {
                setErrors((prev) => ({ ...prev, joining_date: "Future date is not allowed" }));
                return;
            }
        }
        if (name === "dob" && value) {
            const selectedDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (selectedDate >= today) {
                setErrors(prevErrors => ({ ...prevErrors, dob: "Date of birth must be a past date" }));
                return;
            }
        }

        setInputState({ ...inputState, [name]: value.trim ? (value.trim() ? "green" : "") : (value ? "green" : "") });
        setFormData({ ...formData, [name]: value });
        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (value && !(Array.isArray(value) && value.length === 0)) delete updatedErrors[name];
            return updatedErrors;
        });
    };

    const [loading, setLoading] = useState(false);
    const Role = sessionStorage.getItem("role");

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setShowProgressModal(true);
            setLoading(true);
            setUploadProgress(0);

            const loadingToastId = toast.loading("Loading: Please wait...");

            let progress = 0;
            const interval = setInterval(() => {
                progress += 10;
                if (progress >= 100) {
                    progress = 100;
                    clearInterval(interval);
                }
                setUploadProgress(progress);
            }, 100);

            try {
                let res = await axios.post(`${BASE_URL}/siteproject/employee/`, formData);
                if (res.status === 200) {
                    await getSiteEmployee();
                    handleClose();
                    toast.dismiss(loadingToastId);
                    toast.success("Submitted Successfully");
                }
            } catch (err) {
                clearInterval(interval);
                setUploadProgress(0);
                handleErrorToast(err);
            } finally {
                clearInterval(interval);
                setTimeout(() => {
                    setShowProgressModal(false);
                }, 1500);
                setLoading(false);
                toast.dismiss(loadingToastId);
            }
        } else {
            const firstErrorTab = sections.find(s => tabHasMissingFields(s.id))?.id;
            if (firstErrorTab) {
                setActiveSection(firstErrorTab);
                setTabValidationAttempted(prev => ({ ...prev, [firstErrorTab]: true }));
            }
        }
    };

    const getRoleData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/siteproject/rolebysubcompany/${companyId}/`);
            setRoleList(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        getRoleData();
    }, []);

    const roleOption = [...roleList.map((i) => ({ value: i.role_name, label: i.role_name }))];

    const ProjectListOption = siteProjectList
        ?.sort((a, b) => a.site_prcode.localeCompare(b.site_prcode))
        .map((i) => ({ value: i.id, label: `${i.site_prcode} - ${i.project_short_name}` }));

    const handleChangecc = (selected) => {
        const multiple_project = selected.length > 0 ? selected.map((item) => item.value) : [];
        setFormData((prevState) => ({ ...prevState, multiple_project }));
        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (multiple_project.length > 0) delete updatedErrors.multiple_project;
            return updatedErrors;
        });
    };

    const handleProjectChange = (selectedOption) => {
        if (!selectedOption) {
            setFormData({ ...formData, project: "" });
        } else {
            setFormData({ ...formData, project: selectedOption.value });
        }
        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            delete updatedErrors.project;
            return updatedErrors;
        });
    };

    const handleChange = (e) => setRequiredFields(e.target.checked);

    useEffect(() => {
        if (formData.inactive_date) {
            setFormData((prev) => ({ ...prev, employee_status: "inactive" }));
        }
    }, [formData.inactive_date]);

    const designationOptions = designationList
        ?.sort((a, b) => a.title?.localeCompare(b.title, "en", { ignorePunctuation: true }))
        .map(i => ({ value: i.id, label: i.title }));

    const maritalStatusOptions = [
        { value: "Single", label: "Single" },
        { value: "Married", label: "Married" },
        { value: "Widowed", label: "Widowed" },
        { value: "Divorced", label: "Divorced" },
        { value: "Separated", label: "Separated" },
    ];

    const relationshipOptions = [
        { value: "Father", label: "Father" },
        { value: "Mother", label: "Mother" },
        { value: "Spouse", label: "Spouse" },
        { value: "Son", label: "Son" },
        { value: "Daughter", label: "Daughter" },
        { value: "Other", label: "Other" },
    ];

    // ---------- Tab navigation with validation (mirrors AddProjectDetails) ----------
    const goToNextTab = () => {
        const currentIndex = sections.findIndex(s => s.id === activeSection);
        const nextTab = sections[currentIndex + 1]?.id;

        if (!validateCurrentTab()) {
            const formContainer = document.querySelector('.modern-modal-body');
            if (formContainer) formContainer.scrollTop = 0;
            return;
        }

        if (nextTab) {
            setErrors(prev => {
                const nextTabFields = getTabRequiredFields(nextTab);
                const filtered = { ...prev };
                nextTabFields.forEach(field => delete filtered[field]);
                return filtered;
            });
        }

        if (currentIndex < sections.length - 1) {
            setActiveSection(nextTab);
        }
    };

    const goToPreviousTab = () => {
        const idx = sections.findIndex(s => s.id === activeSection);
        if (idx > 0) {
            setActiveSection(sections[idx - 1].id);
        }
    };

    useEffect(() => {
        if (role !== "Admin") {
            setShowRequired(false);
        }
    }, [role]);

    const renderEmployeeSection = () => (
        <div className="form-section">
            <div className="section-title">
                <Briefcase size={18} />
                <h4>Employee Details</h4>
            </div>
            <div className="form-grid">
                <div className="form-field">
                    <label><Hash size={14} /> Emp Code<span className="required">*</span></label>
                    <input maxLength={20} type="text" name="emp_code" placeholder="Example-CISK85219" onChange={handleInputChange} value={formData.emp_code} className={errors.emp_code ? "error" : inputState.emp_code ? "success" : ""} />
                    {errors.emp_code && <span className="error-message">{errors.emp_code}</span>}
                </div>

                {companyId != null && companyId !== undefined && (
                    <div className="form-field">
                        <label><Building2 size={14} /> Division</label>
                        <Select
                            name="division"
                            options={siteSubCompanyList
                                ?.sort((a, b) => a.title?.localeCompare(b.title, 'en', { ignorePunctuation: true }))
                                .map((i) => ({ value: i.id, label: i.name }))}
                            isSearchable
                            isClearable
                            placeholder="Select a Division"
                            value={
                                siteSubCompanyList
                                    ?.map((i) => ({ value: i.id, label: i.name }))
                                    .find((opt) => opt.value === formData.division) || null
                            }
                            onChange={(selected) => {
                                handleInputChange({ target: { name: "division", value: selected ? selected.value : "" } });
                            }}
                            styles={CSSForSelectModern}
                        />
                        {errors.division && <span className="error-message">{errors.division}</span>}
                    </div>
                )}

                <div className="form-field">
                    <label><Users size={14} /> Employee Type {requireFields && <span className="required">*</span>}</label>
                    <Select
                        name="professional_type"
                        options={[
                            { value: "Key Professional", label: "Key Professional" },
                            { value: "Sub Professional", label: "Sub Professional" },
                            { value: "Support Staff", label: "Support Staff" },
                            { value: "Ho Staff", label: "Ho Staff" },
                        ]}
                        isSearchable
                        isClearable
                        placeholder="Select a Professional"
                        value={
                            [
                                { value: "Key Professional", label: "Key Professional" },
                                { value: "Sub Professional", label: "Sub Professional" },
                                { value: "Support Staff", label: "Support Staff" },
                                { value: "Ho Staff", label: "Ho Staff" },
                            ].find((opt) => opt.value === formData.professional_type) || null
                        }
                        onChange={(selected) => {
                            handleInputChange({ target: { name: "professional_type", value: selected ? selected.value : "" } });
                        }}
                        styles={CSSForSelectModern}
                    />
                    {errors.professional_type && <span className="error-message">{errors.professional_type}</span>}
                </div>

                <div className="form-field">
                    <label><Briefcase size={14} /> Employment Type {requireFields && <span className="required">*</span>}</label>
                    <Select
                        name="employment_type"
                        options={[
                            { value: "temporary", label: "Intermediate" },
                            { value: "permanent", label: "Full Time" },
                        ]}
                        isSearchable
                        isClearable
                        placeholder="Select Employment Type"
                        value={
                            [
                                { value: "temporary", label: "Intermediate" },
                                { value: "permanent", label: "Full Time" },
                            ].find((opt) => opt.value === formData.employment_type) || null
                        }
                        onChange={(selected) => {
                            handleInputChange({ target: { name: "employment_type", value: selected ? selected.value : "" } });
                        }}
                        styles={CSSForSelectModern}
                    />
                    {errors.employment_type && <span className="error-message">{errors.employment_type}</span>}
                </div>

                {!(siteRole === "SiteManagement" || formData.professional_type !== "Key Professional") && (
                    <div className="form-field">
                        <label><IndianRupee size={14} /> Replacement Deduction</label>
                        <input type="text" min={0} maxLength={10} name="replacement_deduction" placeholder="Replacement Deduction" onChange={handleInputChange} value={formData.replacement_deduction} className={errors.replacement_deduction ? "error" : inputState.replacement_deduction ? "success" : ""} />
                        {errors.replacement_deduction && <span className="error-message">{errors.replacement_deduction}</span>}
                    </div>
                )}

                <div className="form-field">
                    <label><UserCog size={14} /> Employee Role {requireFields && <span className="required">*</span>}</label>
                    <Select
                        name="emp_role"
                        options={roleOption}
                        isSearchable
                        isClearable
                        placeholder="Select Role"
                        value={roleOption.find((opt) => opt.value === formData.emp_role) || null}
                        onChange={(selected) => {
                            handleInputChange({ target: { name: "emp_role", value: selected ? selected.value : "" } });
                        }}
                        styles={CSSForSelectModern}
                    />
                    {errors.emp_role && <span className="error-message">{errors.emp_role}</span>}
                </div>

                <div className="form-field">
                    <label><Building2 size={14} /> Department {requireFields && <span className="required">*</span>}</label>
                    <Select
                        options={departmentOptions}
                        isSearchable isClearable placeholder="Select"
                        value={departmentOptions.find((opt) => opt.value === formData.emp_department)}
                        onChange={(selectedOption) => {
                            setFormData({ ...formData, emp_department: selectedOption ? selectedOption.value : "" });
                            setInputState({ ...inputState, emp_department: selectedOption ? "green" : "" });
                            setErrors((prev) => { const u = { ...prev }; if (selectedOption) delete u.emp_department; return u; });
                        }}
                        styles={CSSForSelectModern}
                    />
                    {errors.emp_department && <span className="error-message">{errors.emp_department}</span>}
                </div>

                <div className="form-field">
                    <label> Joining Date {requireFields && <span className="required">*</span>}</label>
                    <input type="date" name="joining_date" onChange={handleInputChange} value={formData.joining_date} max={new Date().toISOString().split("T")[0]} className={errors.joining_date ? "error" : inputState.joining_date ? "success" : ""} />
                    {errors.joining_date && <span className="error-message">{errors.joining_date}</span>}
                </div>

                <div className="form-field">
                    <label> Exit Date {requireFields && <span className="required">*</span>}</label>
                    <input type="date" name="inactive_date" min={formData.joining_date} onChange={handleInputChange} value={formData.inactive_date} className={errors.inactive_date ? "error" : inputState.inactive_date ? "success" : ""} />
                    {errors.inactive_date && <span className="error-message">{errors.inactive_date}</span>}
                </div>

                {siteRole !== "SiteManagement" && (
                    <div className="form-field">
                        <label><Activity size={14} /> Employee Status</label>
                        <Select
                            name="employee_status"
                            options={[
                                { value: "active", label: "Active" },
                                { value: "inactive", label: "In Active" },
                            ]}
                            isSearchable
                            isClearable
                            placeholder="Select Option"
                            value={
                                [
                                    { value: "active", label: "Active" },
                                    { value: "inactive", label: "In Active" },
                                ].find((opt) => opt.value === formData.employee_status) || null
                            }
                            onChange={(selected) => {
                                handleInputChange({ target: { name: "employee_status", value: selected ? selected.value : "" } });
                            }}
                            styles={CSSForSelectModern}
                        />
                        {errors.employee_status && <span className="error-message">{errors.employee_status}</span>}
                    </div>
                )}
                <div className="form-field">
                    <label><Tag size={14} /> Designation {requireFields && <span className="required">*</span>} <span className="required" style={{ fontSize: '10px' }}>(Select Division First)</span></label>
                    <Select
                        options={designationOptions}
                        isClearable
                        placeholder={designationList.length === 0 ? "Add Designation First" : "Select Designation"}
                        isSearchable
                        value={designationOptions.find(opt => opt.value === formData.designation)}
                        onChange={(selected) => handleInputChange({ target: { name: "designation", value: selected ? selected.value : "" } })}
                        styles={CSSForSelectModern}
                    />
                    {errors.designation && <span className="error-message">{errors.designation}</span>}
                </div>

                {((role !== "SiteManagement" && formData.employment_type === "permanent") && (formData.emp_role === "SiteManagement" || formData.emp_role === "SiteEmployee")) ? (
                    <div className="form-field">
                        <label><MapPin size={14} /> Site Project <span className="required">*</span></label>
                        <Select
                            options={ProjectListOption}
                            onChange={handleProjectChange}
                            value={formData.project === "null" || formData.project === undefined ? "null" : ProjectListOption.find((option) => option.value === formData.project)}
                            placeholder="Select a Project"
                            isSearchable isClearable
                            noOptionsMessage={() => "No other options"}
                            styles={CSSForSelectModern}
                        />
                        {errors.project && <span className="error-message">{errors.project}</span>}
                    </div>
                ) : ((role !== "SiteManagement" && formData.employment_type === "temporary") && (formData.emp_role === "SiteManagement" || formData.emp_role === "SiteEmployee")) ? (
                    <div className="form-field">
                        <label><MapPin size={14} /> Site Project <span className="required">*</span></label>
                        <Select
                            options={ProjectListOption}
                            onChange={handleChangecc}
                            value={formData.multiple_project?.map(id => ProjectListOption?.find(opt => opt.value === id)).filter(Boolean)}
                            placeholder="Select a Project"
                            isSearchable isMulti isClearable
                            noOptionsMessage={() => "No other options"}
                            styles={CSSForSelectModern}
                        />
                        {errors.multiple_project && <span className="error-message">{errors.multiple_project}</span>}
                    </div>
                ) : null}
            </div>

            <div className="section-navigation-buttons nav-buttons-wrapper">
                {tabValidationAttempted[activeSection] && hasCurrentTabErrors() && (
                    <div className="tab-error-message">
                        <AlertCircle size={14} />
                        <span>{getCurrentTabErrorMessage()}</span>
                    </div>
                )}
                {tabValidationAttempted[activeSection] && !hasCurrentTabErrors() && (
                    <div></div>
                )}
                <button type="button" className="btn-next" onClick={goToNextTab} disabled={tabHasMissingFields('employee')}>
                    Next: Personal Details <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );

    const renderPersonalSection = () => (
        <div className="form-section">
            <div className="section-title">
                <User size={18} />
                <h4>Personal Details</h4>
            </div>
            <div className="form-grid">
                <div className="form-field">
                    <label><User size={14} /> Name<span className="required">*</span></label>
                    <input type="text" maxLength={100} name="name" placeholder="Example-Jhon Doe" onChange={handleInputChange} value={formData.name} className={errors.name ? "error" : inputState.name ? "success" : ""} />
                    {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-field">
                    <label><Mail size={14} /> Email<span className="required">*</span></label>
                    <input type="email" name="email" placeholder="Example-Demo@gmail.com" onChange={handleInputChange} value={formData.email}
                        onInput={(e) => { e.target.value = e.target.value.toLowerCase().trim(); }}
                        className={errors.email ? "error" : inputState.email ? "success" : ""} />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-field">
                    <label><Phone size={14} /> Mobile No.<span className="required">*</span></label>
                    <input type="text" name="mobile" placeholder="Example-7547296250" value={formData.mobile} onChange={handleInputChange} maxLength={10} inputMode="numeric" pattern="[0-9]*" className={errors.mobile ? "error" : inputState.mobile ? "success" : ""} />
                    {errors.mobile && <span className="error-message">{errors.mobile}</span>}
                </div>

                <div className="form-field">
                    <label> Date of Birth <span className="required">*</span></label>
                    <input type="date" name="dob" onChange={handleInputChange} value={formData.dob} max={new Date().toISOString().split("T")[0]} className={errors.dob ? "error" : inputState.dob ? "success" : ""} />
                    {errors.dob && <span className="error-message">{errors.dob}</span>}
                </div>

                <div className="form-field">
                    <label><Users size={14} /> Gender {requireFields && <span className="required">*</span>}</label>
                    <Select
                        name="gender"
                        options={[
                            { value: "male", label: "Male" },
                            { value: "female", label: "Female" },
                            { value: "other", label: "Other" },
                        ]}
                        isSearchable
                        isClearable
                        placeholder="Select Gender"
                        value={
                            [
                                { value: "male", label: "Male" },
                                { value: "female", label: "Female" },
                                { value: "other", label: "Other" },
                            ].find((opt) => opt.value === formData.gender) || null
                        }
                        onChange={(selected) => {
                            handleInputChange({ target: { name: "gender", value: selected ? selected.value : "" } });
                        }}
                        styles={CSSForSelectModern}
                    />
                    {errors.gender && <span className="error-message">{errors.gender}</span>}
                </div>

                <div className="form-field">
                    <label><FileText size={14} /> Qualification</label>
                    <input type="text" maxLength={100} name="qualification" placeholder="Example-B.Tech" onChange={handleInputChange} value={formData.qualification} className={errors.qualification ? "error" : inputState.qualification ? "success" : ""} />
                    {errors.qualification && <span className="error-message">{errors.qualification}</span>}
                </div>

                <div className="form-field">
                    <label><Users size={14} /> Marital Status {requireFields && <span className="required">*</span>}</label>
                    <Select
                        options={maritalStatusOptions}
                        isSearchable placeholder="Select Marital Status"
                        value={maritalStatusOptions.find(opt => opt.value === formData.married_status)}
                        onChange={(selected) => {
                            setFormData(prev => ({ ...prev, married_status: selected ? selected.value : "" }));
                            setInputState(prev => ({ ...prev, married_status: selected ? "green" : "" }));
                            setErrors((prev) => { const u = { ...prev }; if (selected) delete u.married_status; return u; });
                        }}
                        isClearable styles={CSSForSelectModern}
                    />
                    {errors.married_status && <span className="error-message">{errors.married_status}</span>}
                </div>

                <div className="form-field">
                    <label><User size={14} /> Emergency Person Name</label>
                    <input type="text" maxLength={100} name="emergency_person_name" placeholder="Example-Jhon Doe" onChange={handleInputChange} value={formData.emergency_person_name} className={errors.emergency_person_name ? "error" : inputState.emergency_person_name ? "success" : ""} />
                    {errors.emergency_person_name && <span className="error-message">{errors.emergency_person_name}</span>}
                </div>

                <div className="form-field">
                    <label><Phone size={14} /> Emergency Phone Number</label>
                    <input type="number" maxLength={10} min={0} name="emergency_phone_number" placeholder="Example-785236589" onChange={handleInputChange} value={formData.emergency_phone_number} className={errors.emergency_phone_number ? "error" : inputState.emergency_phone_number ? "success" : ""} />
                    {errors.emergency_phone_number && <span className="error-message">{errors.emergency_phone_number}</span>}
                </div>

                <div className="form-field">
                    <label><Users size={14} /> Relation With {formData.name}</label>
                    <Select
                        options={relationshipOptions}
                        isSearchable placeholder="Select Relationship"
                        value={relationshipOptions.find(opt => opt.value === formData.emergency_relation)}
                        onChange={(selected) => handleInputChange({ target: { name: "emergency_relation", value: selected ? selected.value : "" } })}
                        styles={CSSForSelectModern}
                    />
                    {errors.emergency_relation && <span className="error-message">{errors.emergency_relation}</span>}
                </div>

                <div className="form-field">
                    <label><CreditCard size={14} /> Aadhaar Card No.<span className="required">*</span></label>
                    <input type="number" maxLength={100} min={0} name="aadhaar" placeholder="Example-7524 7852 9523" onChange={handleInputChange} value={formData.aadhaar} className={errors.aadhaar ? "error" : inputState.aadhaar ? "success" : ""} />
                    {errors.aadhaar && <span className="error-message">{errors.aadhaar}</span>}
                </div>

                <div className="form-field">
                    <label><Hash size={14} /> UAN Number</label>
                    <input type="text" maxLength={100} name="uan" placeholder="Example-100456789012" onChange={handleInputChange} value={formData.uan} className={errors.uan ? "error" : inputState.uan ? "success" : ""} />
                    {errors.uan && <span className="error-message">{errors.uan}</span>}
                </div>

                <div className="form-field">
                    <label><CreditCard size={14} /> PAN No. {requireFields && <span className="required">*</span>}</label>
                    <input type="text" name="pan" placeholder="ABCDE1234F" maxLength={10} value={formData.pan} onChange={handleInputChange} className={errors.pan ? "error" : inputState.pan ? "success" : ""} />
                    {errors.pan && <span className="error-message">{errors.pan}</span>}
                </div>

                <div className="form-field full-width">
                    <label><MapPin size={14} /> Current Address</label>
                    <textarea name="current_address" maxLength={300} placeholder="Example-Delhi" onChange={handleInputChange} value={formData.current_address} rows={2} className={errors.current_address ? "error" : inputState.current_address ? "success" : ""} />
                    {errors.current_address && <span className="error-message">{errors.current_address}</span>}
                </div>

                <div className="form-field full-width">
                    <label><MapPin size={14} /> Emergency Address</label>
                    <textarea name="emergency_address" maxLength={300} placeholder="Example-Delhi" onChange={handleInputChange} value={formData.emergency_address} rows={2} className={errors.emergency_address ? "error" : inputState.emergency_address ? "success" : ""} />
                    {errors.emergency_address && <span className="error-message">{errors.emergency_address}</span>}
                </div>
            </div>

            <div className="section-navigation-buttons dual-buttons">
                <div className="nav-buttons-wrapper">
                    <button type="button" className="btn-prev" onClick={goToPreviousTab}>
                        <ChevronRight size={16} className="rotate-left" /> Previous
                    </button>
                    {tabValidationAttempted[activeSection] && hasCurrentTabErrors() && (
                        <div className="tab-error-message">
                            <AlertCircle size={14} />
                            <span>{getCurrentTabErrorMessage()}</span>
                        </div>
                    )}
                    <button type="button" className="btn-next" onClick={goToNextTab} disabled={tabHasMissingFields('personal')}>
                        Next: Bank Details <ChevronRight size={16} />
                    </button>
                </div>
            </div>
        </div>
    );

    const renderBankSection = () => (
        <div className="form-section">
            <div className="section-title">
                <CreditCard size={18} />
                <h4>Bank Details</h4>
            </div>
            <div className="form-grid">
                <div className="form-field">
                    <label><Hash size={14} /> Account Number {requireFields && <span className="required">*</span>}</label>
                    <input type="number" min={0} name="account_number" placeholder="Example-75206516316854" onChange={handleInputChange} value={formData.account_number} className={errors.account_number ? "error" : inputState.account_number ? "success" : ""} />
                    {errors.account_number && <span className="error-message">{errors.account_number}</span>}
                </div>

                <div className="form-field">
                    <label><Hash size={14} /> IFSC code {requireFields && <span className="required">*</span>}</label>
                    <input type="text" maxLength={15} name="ifsc_code" placeholder="Example-ABCIN0000691" onChange={handleInputChange} value={formData.ifsc_code} className={errors.ifsc_code ? "error" : inputState.ifsc_code ? "success" : ""} />
                    {errors.ifsc_code && <span className="error-message">{errors.ifsc_code}</span>}
                </div>

                <div className="form-field">
                    <label><User size={14} /> Account Holder Name {requireFields && <span className="required">*</span>}</label>
                    <input type="text" maxLength={60} name="account_holder_name" placeholder="Example-ABC" onChange={handleInputChange} value={formData.account_holder_name} className={errors.account_holder_name ? "error" : inputState.account_holder_name ? "success" : ""} />
                    {errors.account_holder_name && <span className="error-message">{errors.account_holder_name}</span>}
                </div>

                <div className="form-field">
                    <label><Building2 size={14} /> Bank Name {requireFields && <span className="required">*</span>}</label>
                    <input type="text" maxLength={60} name="bank_name" placeholder="Example-ABC Bank" onChange={handleInputChange} value={formData.bank_name} className={errors.bank_name ? "error" : inputState.bank_name ? "success" : ""} />
                    {errors.bank_name && <span className="error-message">{errors.bank_name}</span>}
                </div>

                <div className="form-field full-width">
                    <label><MapPin size={14} /> Bank Address</label>
                    <textarea maxLength={100} name="branch_address" placeholder="Example-Delhi" onChange={handleInputChange} value={formData.branch_address} rows={2} className={errors.branch_address ? "error" : inputState.branch_address ? "success" : ""} />
                    {errors.branch_address && <span className="error-message">{errors.branch_address}</span>}
                </div>
            </div>

            <div className="form-actions">
                <button type="button" className="btn-prev" onClick={goToPreviousTab}>
                    <ChevronRight size={16} className="rotate-left" /> Previous: Personal Details
                </button>
                <div className="submit-buttons">
                    <button type="button" className="btn-secondaryy" onClick={handleClose}>
                        Cancel
                    </button>
                    <button type="submit" className="btn-primaryy" disabled={loading || tabHasMissingFields('bank')}>
                        {loading ? <Loader size={18} className="spinning" /> : <Save size={18} />}
                        <span>{loading ? 'Submitting...' : 'Submit'}</span>
                    </button>
                </div>
            </div>

            {tabValidationAttempted[activeSection] && hasCurrentTabErrors() && (
                <div className="tab-error-message" style={{ marginTop: '12px' }}>
                    <AlertCircle size={14} />
                    <span>{getCurrentTabErrorMessage()}</span>
                </div>
            )}
        </div>
    );

    const renderCurrentSection = () => {
        switch (activeSection) {
            case 'employee': return renderEmployeeSection();
            case 'personal': return renderPersonalSection();
            case 'bank': return renderBankSection();
            default: return renderEmployeeSection();
        }
    };



    return (
        <>
            <AddModalButton onClick={handleShowRequiredFieldModal} title="Site Employee" />
            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Add" />
            ) : (
                <>


                    {role === "Admin" && (
                        <Modal show={showrequired} onHide={handleHideRequiredFieldModal} dialogClassName="modern-required-modal" centered>
                            <Modal.Header className="modern-modal-header requirement-modal-header">
                                <div className="header-content">
                                    <div className="requirement-header-icon">
                                        <ShieldCheck size={22} />
                                    </div>
                                    <div>
                                        <Modal.Title>Form Requirement Mode</Modal.Title>
                                        <p className="header-subtitle" style={{ color: "white" }}>Choose how strict the form validation should be</p>
                                    </div>
                                </div>
                                <button className="close-btn" onClick={handleHideRequiredFieldModal}>
                                    <X size={20} />
                                </button>
                            </Modal.Header>

                            <Modal.Body className="modern-modal-body requirement-modal-body">
                                <div className="requirement-mode-grid">
                                    <div
                                        className={`requirement-mode-card ${!requireFields ? 'selected' : ''}`}
                                        onClick={() => setRequiredFields(false)}
                                    >
                                        <div className="requirement-mode-icon basic">
                                            <Zap size={20} />
                                        </div>
                                        <h5>Basic Fields Only</h5>
                                        <p>Only essential fields required — code, name, role, email, mobile, aadhaar, DOB.</p>
                                        <div className="requirement-mode-check">
                                            {!requireFields && <Check size={14} />}
                                        </div>
                                    </div>

                                    <div
                                        className={`requirement-mode-card ${requireFields ? 'selected' : ''}`}
                                        onClick={() => setRequiredFields(true)}
                                    >
                                        <div className="requirement-mode-icon strict">
                                            <ShieldCheck size={20} />
                                        </div>
                                        <h5>All Fields Required</h5>
                                        <p>Employee, personal and bank details all become mandatory before submission.</p>
                                        <div className="requirement-mode-check">
                                            {requireFields && <Check size={14} />}
                                        </div>
                                    </div>
                                </div>

                                <div className="requirement-mode-summary">
                                    <Info size={14} />
                                    <span>
                                        Currently in <strong>{requireFields ? "Strict" : "Basic"}</strong> mode — you can change this anytime before submitting.
                                    </span>
                                </div>

                                <div className="submit-buttons" style={{ justifyContent: 'flex-end', marginTop: '20px' }}>
                                    <button type="button" className="btn-secondaryy" onClick={handleClose}>Cancel</button>
                                    <button
                                        type="button"
                                        className="btn-primaryy"
                                        onClick={() => { handleHideRequiredFieldModal(); setShow(true); }}
                                    >
                                        Continue <ChevronRight size={16} />
                                    </button>
                                </div>
                            </Modal.Body>
                        </Modal>
                    )}

                    {!showrequired && (
                        <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="xl">
                            <Modal.Header className="modern-modal-header">
                                <div className="header-content">
                                    <div style={{ color: "white" }}>
                                        <Users size={24} />
                                    </div>
                                    <div>
                                        <Modal.Title>Add Site Employee</Modal.Title>
                                        <p className="header-subtitle" style={{ color: "white" }}>Fill in the details to add a new employee</p>
                                    </div>
                                </div>
                                <button className="close-btn" onClick={handleClose}>
                                    <X size={20} />
                                </button>
                            </Modal.Header>

                            <Modal.Body className="modern-modal-body">
                                <div className="section-navigation">
                                    {sections.map((section) => {
                                        const Icon = section.icon;
                                        const hasMissing = tabHasMissingFields(section.id);
                                        return (
                                            <button
                                                key={section.id}
                                                className={`section-nav-btn ${activeSection === section.id ? 'active' : ''} ${hasMissing && section.id !== activeSection ? 'has-warning' : ''}`}
                                                onClick={() => {
                                                    if (activeSection !== section.id) {
                                                        if (validateCurrentTab()) {
                                                            setActiveSection(section.id);
                                                        } else {
                                                            const formContainer = document.querySelector('.modern-modal-body');
                                                            if (formContainer) formContainer.scrollTop = 0;
                                                        }
                                                    }
                                                }}
                                            >
                                                <Icon size={16} />
                                                <span>{section.label}</span>
                                                {hasMissing && section.id !== activeSection && (
                                                    <div className="warning-dot" title="Missing required fields" />
                                                )}
                                                {activeSection === section.id && <ChevronRight size={14} className="nav-arrow" />}
                                            </button>
                                        );
                                    })}
                                </div>

                                <form onSubmit={handleFormSubmit} className="project-form">
                                    {renderCurrentSection()}
                                </form>

                                <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                            </Modal.Body>
                        </Modal>
                    )}
                </>
            )}
        </>
    );
};


const UpdateSiteEmployee = ({ companyList, i, getSiteEmployee, siteProjectList, siteSubCompanyList, projectId }) => {

    const companyId = sessionStorage.getItem("sitemanagement_project_sub_company");
    const role = sessionStorage.getItem('sitemanagement_role');
    const siteSubcompany = sessionStorage.getItem("sitemanagement_project_sub_company");
    const siteRole = sessionStorage.getItem("sitemanagement_role");
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [show, setShow] = useState(false);
    const [showrequired, setShowRequired] = useState(false);
    const [requireFields, setRequiredFields] = useState(false);
    const [buffer, setBuffering] = useState(true);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [designationList, setDesignationList] = useState([]);
    const [divisionList, setDivisionList] = useState([]);
    const [roleList, setRoleList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const [activeSection, setActiveSection] = useState('employee');
    const [tabValidationAttempted, setTabValidationAttempted] = useState({});

    const sections = [
        { id: 'employee', label: 'Employee Details', icon: Briefcase },
        { id: 'personal', label: 'Personal Details', icon: User },
        { id: 'bank', label: 'Bank Details', icon: CreditCard },
    ];

    const [formData, setFormData] = useState({
        emp_code: "",
        emp_role: null,
        name: "",
        email: "",
        mobile: "",
        designation: "",
        professional_type: "",
        current_address: "",
        joining_date: null,
        employee_status: "active",
        sub_company: companyId ? companyId : "",
        division: "",
        multiple_project: [],
        employment_type: "",
        project: projectId !== "null" ? projectId : null,
        dob: null,
        emp_department: "",
        gender: "",
        married_status: "",
        qualification: "",
        emergency_person_name: "",
        emergency_phone_number: "",
        emergency_relation: "",
        emergency_address: "",
        account_number: "",
        account_holder_name: "",
        ifsc_code: "",
        bank_name: "",
        branch_address: "",
        aadhaar: "",
        uan: "",
        pan: "",
        replacement_deduction: "",
        inactive_date: null,
    });

    // ---------- Open flow: Admin sees requirement modal first ----------
    const handleOpenClick = () => {
        if (role === "Admin") {
            setShowRequired(true);
        } else {
            handleShow();
        }
    };

    const handleShow = async () => {
        setFormData(i);
        setActiveSection('employee');
        setTabValidationAttempted({});
        setErrors({});
        await getUserDetails();
        setShow(true);
    };

    const handleClose = () => {
        setShow(false);
        setActiveSection('employee');
        setTabValidationAttempted({});
        setErrors({});
    };

    const handleHideRequiredFieldModal = () => setShowRequired(false);

    const getDivision = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/siteproject/divisionfilter/a2b6154c-5c4a-4bcb-a4ec-b151c5c652b5/`);
            setDivisionList(response.data);
        } catch (error) {
            alert(error);
        }
    };

    useEffect(() => {
        if (show === true) {
            getDivision();
        }
    }, [show]);

    const getUserDetails = async () => {
        setBuffering(true);
        try {
            const encodedEmpCode = encodeURIComponent(i.emp_code);
            const res = await axios.get(`${BASE_URL}/siteproject/employeebyempcode/${encodedEmpCode}/`);
            const filteredData = res.data;
            delete filteredData.profilepic;
            setFormData(filteredData);
        } catch (err) {
            handleErrorToast(err);
        } finally {
            setBuffering(false);
        }
    };

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            employee_status: (prev.inactive_date !== null && prev.inactive_date !== "") ? "inactive" : "active"
        }));
    }, [formData.inactive_date]);

    useEffect(() => {
        if (formData.employee_status === "active") {
            setFormData((prev) => ({ ...prev, inactive_date: "" }));
        }
    }, [formData.employee_status]);

    useEffect(() => {
        if (formData.inactive_date === "") {
            setFormData((prev) => ({ ...prev, inactive_date: null }));
        }
    }, [formData.inactive_date]);

    useEffect(() => {
        setFormData((prev) => ({ ...prev, sub_company: companyId }));
    });

    useEffect(() => {
        if (formData.employment_type === "temporary") {
            setFormData((prev) => ({ ...prev, project: null }));
        } else if (formData.employment_type === "permanent") {
            setFormData((prev) => ({ ...prev, multiple_project: [] }));
        }
    }, [formData.employment_type]);

    useEffect(() => {
        setFormData((prev) => ({ ...prev, project: null, multiple_project: [] }));
    }, [formData.emp_role]);

    useEffect(() => {
        if (formData.division != null && formData.division !== undefined && formData.division !== "" && show === true) {
            const getDesignation = async () => {
                try {
                    const response = await axios.get(`${BASE_URL}/siteproject/designationfilterbysubcompanycount/${formData.division}/null/${siteSubcompany}/`);
                    setDesignationList(response.data);
                } catch (error) {
                    console.error("Error fetching designations:", error);
                }
            };
            getDesignation();
        }
    }, [formData.division, show, siteSubcompany]);

    useEffect(() => {
        setFormData((prev) => ({ ...prev, replacement_deduction: "" }));
    }, [formData.professional_type]);

    // ---------- Field labels ----------
    const fieldLabels = {
        emp_code: 'Emp Code',
        emp_role: 'Employee Role',
        professional_type: 'Employee Type',
        employment_type: 'Employment Type',
        emp_department: 'Department',
        joining_date: 'Joining Date',
        designation: 'Designation',
        division: 'Division',
        name: 'Name',
        email: 'Email',
        mobile: 'Mobile No.',
        gender: 'Gender',
        married_status: 'Marital Status',
        aadhaar: 'Aadhaar Card No.',
        pan: 'PAN No.',
        dob: 'Date of Birth',
        account_number: 'Account Number',
        ifsc_code: 'IFSC Code',
        account_holder_name: 'Account Holder Name',
        bank_name: 'Bank Name',
        project: 'Site Project',
        multiple_project: 'Site Projects',
    };

    // ---------- Tab-wise required fields ----------
    const getTabRequiredFields = (tabId, data = formData) => {
        let fields = [];

        if (requireFields) {
            const map = {
                employee: ['emp_code', 'emp_role', 'professional_type', 'employment_type', 'emp_department', 'joining_date', 'designation', ...(siteRole === "SiteManagement" ? [] : ['division'])],
                personal: ['name', 'email', 'mobile', 'gender', 'account_number', 'account_holder_name', 'ifsc_code', 'bank_name', 'aadhaar', 'pan'],
                bank: [],
            };
            fields = map[tabId] || [];
        } else {
            const map = {
                employee: ['emp_code', 'emp_role'],
                personal: ['name', 'email', 'aadhaar', 'mobile'],
                bank: [],
            };
            fields = map[tabId] || [];

            if (tabId === 'employee') {
                if (data.employment_type === "temporary" && data.emp_role === "SiteManagement") {
                    fields = [...fields, 'multiple_project'];
                }
                if (data.employment_type === "permanent" && data.emp_role === "SiteManagement") {
                    fields = [...fields, 'project'];
                }
            }
        }

        return fields;
    };

    const isFieldEmpty = (field, data = formData) => {
        const value = data[field];
        if (Array.isArray(value)) return value.length === 0;
        return value === null || value === undefined || value === "null" || (typeof value === 'string' && value.trim() === "");
    };

    const tabHasMissingFields = (tabId) => getTabRequiredFields(tabId).some((field) => isFieldEmpty(field));

    const validateCurrentTab = () => {
        const currentTab = activeSection;
        const fieldsToValidate = getTabRequiredFields(currentTab);
        const newErrors = {};

        fieldsToValidate.forEach((field) => {
            if (isFieldEmpty(field)) {
                newErrors[field] = `${fieldLabels[field] || field} is required!`;
            }
        });

        setErrors(prev => {
            const updated = { ...prev, ...newErrors };
            fieldsToValidate.forEach((field) => {
                if (!isFieldEmpty(field)) delete updated[field];
            });
            return updated;
        });

        setTabValidationAttempted(prev => ({ ...prev, [currentTab]: true }));

        return Object.keys(newErrors).length === 0;
    };

    const hasCurrentTabErrors = () => getTabRequiredFields(activeSection).some(field => errors[field]);

    const getCurrentTabErrorMessage = () => {
        const missingFields = getTabRequiredFields(activeSection)
            .filter(field => isFieldEmpty(field))
            .map(field => fieldLabels[field] || field);

        if (missingFields.length === 0) return null;
        if (missingFields.length === 1) return `${missingFields[0]} is required to proceed`;

        const tabLabel = sections.find(s => s.id === activeSection)?.label || activeSection;
        return `Please fill all required fields in ${tabLabel} before proceeding`;
    };

    const validateForm = () => {
        let requiredFields = [];
        if (requireFields) {
            requiredFields = [
                'emp_code', 'name', 'employment_type', 'email', 'mobile', 'designation',
                'professional_type', 'joining_date', 'gender', 'account_number', 'account_holder_name',
                'ifsc_code', 'bank_name', 'aadhaar', 'pan', 'emp_department', 'emp_role', 'division',
            ].filter(Boolean);
        } else {
            requiredFields = ['emp_code', 'name', 'emp_role', 'email', 'aadhaar', 'mobile'].filter(Boolean);
        }

        if (siteRole === "SiteManagement") {
            requiredFields = requiredFields.filter(field => field !== 'division');
        }

        const newErrors = requiredFields.reduce((acc, field) => {
            if (!formData[field] || formData[field] === "null" || (Array.isArray(formData[field]) && formData[field].length === 0)) {
                acc[field] = `${fieldLabels[field] || field} is required!`;
            }
            return acc;
        }, {});

        if (formData.employment_type === "temporary" && formData.emp_role === "SiteManagement" && (!Array.isArray(formData.multiple_project) || formData.multiple_project.length === 0)) {
            newErrors["multiple_project"] = "Projects are required!";
        }
        if (formData.employment_type === "permanent" && formData.emp_role === "SiteManagement" && (!formData.project || formData.project.trim() === "")) {
            newErrors["project"] = "Project is required!";
        }

        setErrors(newErrors);
        setIsSubmitted(true);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "dob" && value) {
            const selectedDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (selectedDate >= today) {
                setErrors(prevErrors => ({ ...prevErrors, dob: "Date of birth must be a past date" }));
                return;
            }
        }

        setInputState({ ...inputState, [name]: value?.trim ? (value.trim() ? "green" : "") : (value ? "green" : "") });
        setFormData({ ...formData, [name]: value });
        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (value && !(Array.isArray(value) && value.length === 0)) delete updatedErrors[name];
            return updatedErrors;
        });
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (validateForm()) {
            setShowProgressModal(true);
            setLoading(true);
            setUploadProgress(0);

            const loadingToastId = toast.loading("Loading: Please wait...");

            let progress = 0;
            const interval = setInterval(() => {
                progress += 10;
                if (progress >= 100) {
                    progress = 100;
                    clearInterval(interval);
                }
                setUploadProgress(progress);
            }, 100);

            try {
                let res = await axios.put(`${BASE_URL}/siteproject/employee/${i.id}/`, formData);
                if (res.status === 200) {
                    await getSiteEmployee();
                    handleClose();
                    toast.dismiss(loadingToastId);
                    toast.success("Updated Successfully");
                }
            } catch (err) {
                clearInterval(interval);
                setUploadProgress(0);
                handleErrorToast(err);
            } finally {
                clearInterval(interval);
                setTimeout(() => {
                    setShowProgressModal(false);
                }, 1500);
                setLoading(false);
                toast.dismiss(loadingToastId);
            }
        } else {
            const firstErrorTab = sections.find(s => tabHasMissingFields(s.id))?.id;
            if (firstErrorTab) {
                setActiveSection(firstErrorTab);
                setTabValidationAttempted(prev => ({ ...prev, [firstErrorTab]: true }));
            }
        }
    };

    const getRoleData = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/siteproject/rolebysubcompany/${siteSubcompany}/`);
            setRoleList(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (show === true) getRoleData();
    }, [show]);

    const roleOption = [...roleList.map((i) => ({ value: i.role_name, label: i.role_name }))];

    const ProjectListOption = siteProjectList
        ?.sort((a, b) => a.site_prcode.localeCompare(b.site_prcode))
        .map((i) => ({ value: i.id, label: `${i.site_prcode} - ${i.project_short_name}` }));

    const handleChangecc = (selected) => {
        const multiple_project = selected.length > 0 ? selected.map((item) => item.value) : [];
        setFormData((prevState) => ({ ...prevState, multiple_project }));
        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (multiple_project.length > 0) delete updatedErrors.multiple_project;
            return updatedErrors;
        });
    };

    const handleProjectChange = (selectedOption) => {
        if (!selectedOption) {
            setFormData({ ...formData, project: "" });
        } else {
            setFormData({ ...formData, project: selectedOption.value });
        }
        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            delete updatedErrors.project;
            return updatedErrors;
        });
    };

    const getMultipleprojectList = () => {
        if (formData.multiple_project?.length > 0) {
            return formData.multiple_project
                .map((i) => ProjectListOption?.find((option) => option.value === i))
                .filter((option) => option);
        }
        return [];
    };

    useEffect(() => {
        if (formData.inactive_date) {
            setFormData((prev) => ({ ...prev, employee_status: "inactive" }));
        }
    }, [formData.inactive_date]);

    const designationOptions = designationList
        ?.sort((a, b) => a.title?.localeCompare(b.title, "en", { ignorePunctuation: true }))
        .map(i => ({ value: i.id, label: i.title }));

    const maritalStatusOptions = [
        { value: "Single", label: "Single" },
        { value: "Married", label: "Married" },
        { value: "Widowed", label: "Widowed" },
        { value: "Divorced", label: "Divorced" },
        { value: "Separated", label: "Separated" },
    ];

    const relationshipOptions = [
        { value: "Father", label: "Father" },
        { value: "Mother", label: "Mother" },
        { value: "Spouse", label: "Spouse" },
        { value: "Son", label: "Son" },
        { value: "Daughter", label: "Daughter" },
        { value: "Other", label: "Other" },
    ];

    const departmentOptions = [
        { label: "Accounts", value: "Accounts" },
        { label: "Administration", value: "Administration" },
        { label: "Auxiliary", value: "Auxiliary" },
        { label: "BD", value: "BD" },
        { label: "Contracts", value: "Contracts" },
        { label: "Environmental Engineer", value: "Environmental Engineer" },
        { label: "Equipment", value: "Equipment" },
        { label: "Highway", value: "Highway" },
        { label: "Humane Resource", value: "Humane Resource" },
        { label: "Intermittent", value: "Intermittent" },
        { label: "IT", value: "IT" },
        { label: "Lab", value: "Lab" },
        { label: "Management", value: "Management" },
        { label: "Project Co-ordination", value: "Project Co-ordination" },
        { label: "Quantity Survey", value: "Quantity Survey" },
        { label: "Road Safety", value: "Road Safety" },
        { label: "Structure", value: "Structure" },
        { label: "Support Staff", value: "Support Staff" },
    ];

    const goToNextTab = () => {
        const currentIndex = sections.findIndex(s => s.id === activeSection);
        const nextTab = sections[currentIndex + 1]?.id;

        if (!validateCurrentTab()) {
            const formContainer = document.querySelector('.modern-modal-body');
            if (formContainer) formContainer.scrollTop = 0;
            return;
        }

        if (nextTab) {
            setErrors(prev => {
                const nextTabFields = getTabRequiredFields(nextTab);
                const filtered = { ...prev };
                nextTabFields.forEach(field => delete filtered[field]);
                return filtered;
            });
        }

        if (currentIndex < sections.length - 1) {
            setActiveSection(nextTab);
        }
    };

    const goToPreviousTab = () => {
        const idx = sections.findIndex(s => s.id === activeSection);
        if (idx > 0) {
            setActiveSection(sections[idx - 1].id);
        }
    };

    const renderEmployeeSection = () => (
        <div className="form-section">
            <div className="section-title">
                <Briefcase size={18} />
                <h4>Employee Details</h4>
            </div>
            <div className="form-grid">
                <div className="form-field">
                    <label><Hash size={14} /> Emp Code<span className="required">*</span></label>
                    <input maxLength={20} type="text" name="emp_code" placeholder="Example-CISK85219" onChange={handleInputChange} value={formData.emp_code} className={errors.emp_code ? "error" : inputState.emp_code ? "success" : ""} />
                    {errors.emp_code && <span className="error-message">{errors.emp_code}</span>}
                </div>

                {companyId != null && companyId !== undefined && (
                    <div className="form-field">
                        <label><Building2 size={14} /> Division {requireFields && siteRole !== "SiteManagement" && <span className="required">*</span>}</label>
                        <Select
                            name="division"
                            options={divisionList
                                ?.sort((a, b) => a.title?.localeCompare(b.title, 'en', { ignorePunctuation: true }))
                                .map((i) => ({ value: i.id, label: i.name }))}
                            isSearchable
                            isClearable
                            placeholder="Select a Division"
                            value={
                                divisionList
                                    ?.map((i) => ({ value: i.id, label: i.name }))
                                    .find((opt) => opt.value === formData.division) || null
                            }
                            onChange={(selected) => {
                                handleInputChange({ target: { name: "division", value: selected ? selected.value : "" } });
                            }}
                            styles={CSSForSelectModern}
                        />
                        {errors.division && <span className="error-message">{errors.division}</span>}
                    </div>
                )}

                <div className="form-field">
                    <label><Users size={14} /> Employee Type {requireFields && <span className="required">*</span>}</label>
                    <Select
                        name="professional_type"
                        options={[
                            { value: "Key Professional", label: "Key Professional" },
                            { value: "Sub Professional", label: "Sub Professional" },
                            { value: "Support Staff", label: "Support Staff" },
                            { value: "Ho Staff", label: "Ho Staff" },
                        ]}
                        isSearchable
                        isClearable
                        placeholder="Select a Professional"
                        value={
                            [
                                { value: "Key Professional", label: "Key Professional" },
                                { value: "Sub Professional", label: "Sub Professional" },
                                { value: "Support Staff", label: "Support Staff" },
                                { value: "Ho Staff", label: "Ho Staff" },
                            ].find((opt) => opt.value === formData.professional_type) || null
                        }
                        onChange={(selected) => {
                            handleInputChange({ target: { name: "professional_type", value: selected ? selected.value : "" } });
                        }}
                        styles={CSSForSelectModern}
                    />
                    {errors.professional_type && <span className="error-message">{errors.professional_type}</span>}
                </div>

                <div className="form-field">
                    <label><Briefcase size={14} /> Employment Type {requireFields && <span className="required">*</span>}</label>
                    <Select
                        name="employment_type"
                        options={[
                            { value: "temporary", label: "Intermediate" },
                            { value: "permanent", label: "Full Time" },
                        ]}
                        isSearchable
                        isClearable
                        placeholder="Select Employment Type"
                        value={
                            [
                                { value: "temporary", label: "Intermediate" },
                                { value: "permanent", label: "Full Time" },
                            ].find((opt) => opt.value === formData.employment_type) || null
                        }
                        onChange={(selected) => {
                            handleInputChange({ target: { name: "employment_type", value: selected ? selected.value : "" } });
                        }}
                        styles={CSSForSelectModern}
                    />
                    {errors.employment_type && <span className="error-message">{errors.employment_type}</span>}
                </div>

                {!(siteRole === "SiteManagement" || formData.professional_type !== "Key Professional") && (
                    <div className="form-field">
                        <label><IndianRupee size={14} /> Replacement Deduction</label>
                        <input type="text" min={0} maxLength={10} name="replacement_deduction" placeholder="Replacement Deduction" onChange={handleInputChange} value={formData.replacement_deduction} className={errors.replacement_deduction ? "error" : inputState.replacement_deduction ? "success" : ""} />
                        {errors.replacement_deduction && <span className="error-message">{errors.replacement_deduction}</span>}
                    </div>
                )}

                <div className="form-field">
                    <label><UserCog size={14} /> Employee Role {requireFields && <span className="required">*</span>}</label>
                    <Select
                        name="emp_role"
                        options={roleOption}
                        isSearchable
                        isClearable
                        placeholder="Select Role"
                        value={roleOption.find((opt) => opt.value === formData.emp_role) || null}
                        onChange={(selected) => {
                            handleInputChange({ target: { name: "emp_role", value: selected ? selected.value : "" } });
                        }}
                        styles={CSSForSelectModern}
                    />
                    {errors.emp_role && <span className="error-message">{errors.emp_role}</span>}
                </div>

                <div className="form-field">
                    <label><Building2 size={14} /> Department {requireFields && <span className="required">*</span>}</label>
                    <Select
                        options={departmentOptions}
                        isSearchable isClearable placeholder="Select"
                        value={departmentOptions.find((opt) => opt.value === formData.emp_department)}
                        onChange={(selectedOption) => {
                            setFormData({ ...formData, emp_department: selectedOption ? selectedOption.value : "" });
                            setInputState({ ...inputState, emp_department: selectedOption ? "green" : "" });
                            setErrors((prev) => { const u = { ...prev }; if (selectedOption) delete u.emp_department; return u; });
                        }}
                        styles={CSSForSelectModern}
                    />
                    {errors.emp_department && <span className="error-message">{errors.emp_department}</span>}
                </div>

                <div className="form-field">
                    <label> Joining Date {requireFields && <span className="required">*</span>}</label>
                    <input type="date" name="joining_date" onChange={handleInputChange} value={formData.joining_date} className={errors.joining_date ? "error" : inputState.joining_date ? "success" : ""} />
                    {errors.joining_date && <span className="error-message">{errors.joining_date}</span>}
                </div>

                <div className="form-field">
                    <label> Exit Date</label>
                    <input type="date" name="inactive_date" min={formData.joining_date} onChange={handleInputChange} value={formData.inactive_date} readOnly={!(role === "HR" || role === "Admin")} className={errors.inactive_date ? "error" : inputState.inactive_date ? "success" : ""} />
                    {errors.inactive_date && <span className="error-message">{errors.inactive_date}</span>}
                </div>

                {siteRole !== "SiteManagement" && (
                    <div className="form-field">
                        <label><Activity size={14} /> Employee Status</label>
                        <Select
                            name="employee_status"
                            options={[
                                { value: "active", label: "Active" },
                                { value: "inactive", label: "In Active" },
                            ]}
                            isSearchable
                            isClearable
                            placeholder="Select Option"
                            value={
                                [
                                    { value: "active", label: "Active" },
                                    { value: "inactive", label: "In Active" },
                                ].find((opt) => opt.value === formData.employee_status) || null
                            }
                            onChange={(selected) => {
                                handleInputChange({ target: { name: "employee_status", value: selected ? selected.value : "" } });
                            }}
                            styles={CSSForSelectModern}
                        />
                        {errors.employee_status && <span className="error-message">{errors.employee_status}</span>}
                    </div>
                )}

                <div className="form-field">
                    <label><Tag size={14} /> Designation {requireFields && <span className="required">*</span>} <span className="required" style={{ fontSize: '10px' }}>(Select Division First)</span></label>
                    <Select
                        options={designationOptions}
                        isClearable
                        placeholder={designationList.length === 0 ? "Add Designation First" : "Select Designation"}
                        isSearchable
                        value={designationOptions.find(opt => opt.value === formData.designation)}
                        onChange={(selected) => handleInputChange({ target: { name: "designation", value: selected ? selected.value : "" } })}
                        styles={CSSForSelectModern}
                    />
                    {errors.designation && <span className="error-message">{errors.designation}</span>}
                </div>

                {((role !== "SiteManagement" && formData.employment_type === "permanent") && (formData.emp_role === "SiteManagement" || formData.emp_role === "SiteEmployee")) ? (
                    <div className="form-field">
                        <label><MapPin size={14} /> Site Project <span className="required">*</span></label>
                        <Select
                            options={ProjectListOption}
                            onChange={handleProjectChange}
                            value={formData.project === "null" || formData.project === undefined ? "null" : ProjectListOption.find((option) => option.value === formData.project)}
                            placeholder="Select a Project"
                            isSearchable isClearable
                            noOptionsMessage={() => "No other options"}
                            styles={CSSForSelectModern}
                        />
                        {errors.project && <span className="error-message">{errors.project}</span>}
                    </div>
                ) : ((role !== "SiteManagement" && formData.employment_type === "temporary") && (formData.emp_role === "SiteManagement" || formData.emp_role === "SiteEmployee")) ? (
                    <div className="form-field">
                        <label><MapPin size={14} /> Site Project <span className="required">*</span></label>
                        <Select
                            options={ProjectListOption}
                            onChange={handleChangecc}
                            value={getMultipleprojectList()}
                            placeholder="Select a Project"
                            isSearchable isMulti isClearable
                            noOptionsMessage={() => "No other options"}
                            styles={CSSForSelectModern}
                        />
                        {errors.multiple_project && <span className="error-message">{errors.multiple_project}</span>}
                    </div>
                ) : null}
            </div>

            <div className="section-navigation-buttons nav-buttons-wrapper">
                {tabValidationAttempted[activeSection] && hasCurrentTabErrors() && (
                    <div className="tab-error-message">
                        <AlertCircle size={14} />
                        <span>{getCurrentTabErrorMessage()}</span>
                    </div>
                )}
                {tabValidationAttempted[activeSection] && !hasCurrentTabErrors() && (
                    <div></div>
                )}
                <button type="button" className="btn-next" onClick={goToNextTab} disabled={tabHasMissingFields('employee')}>
                    Next: Personal Details <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );

    const renderPersonalSection = () => (
        <div className="form-section">
            <div className="section-title">
                <User size={18} />
                <h4>Personal Details</h4>
            </div>
            <div className="form-grid">
                <div className="form-field">
                    <label><User size={14} /> Name<span className="required">*</span></label>
                    <input type="text" maxLength={100} name="name" placeholder="Example-Jhon Doe" onChange={handleInputChange} value={formData.name} className={errors.name ? "error" : inputState.name ? "success" : ""} />
                    {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-field">
                    <label><Mail size={14} /> Email<span className="required">*</span></label>
                    <input type="email" name="email" placeholder="Example-Demo@gmail.com" onChange={handleInputChange} value={formData.email}
                        onInput={(e) => { e.target.value = e.target.value.toLowerCase().trim(); }}
                        className={errors.email ? "error" : inputState.email ? "success" : ""} />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-field">
                    <label><Phone size={14} /> Mobile No.<span className="required">*</span></label>
                    <input type="text" name="mobile" placeholder="Example-7547296250" value={formData.mobile} onChange={handleInputChange} maxLength={10} inputMode="numeric" pattern="[0-9]*" className={errors.mobile ? "error" : inputState.mobile ? "success" : ""} />
                    {errors.mobile && <span className="error-message">{errors.mobile}</span>}
                </div>

                <div className="form-field">
                    <label> Date of Birth</label>
                    <input type="date" name="dob" onChange={handleInputChange} value={formData.dob} max={new Date().toISOString().split("T")[0]} className={errors.dob ? "error" : inputState.dob ? "success" : ""} />
                    {errors.dob && <span className="error-message">{errors.dob}</span>}
                </div>

                <div className="form-field">
                    <label><Users size={14} /> Gender {requireFields && <span className="required">*</span>}</label>
                    <Select
                        name="gender"
                        options={[
                            { value: "male", label: "Male" },
                            { value: "female", label: "Female" },
                            { value: "other", label: "Other" },
                        ]}
                        isSearchable
                        isClearable
                        placeholder="Select Gender"
                        value={
                            [
                                { value: "male", label: "Male" },
                                { value: "female", label: "Female" },
                                { value: "other", label: "Other" },
                            ].find((opt) => opt.value === formData.gender) || null
                        }
                        onChange={(selected) => {
                            handleInputChange({ target: { name: "gender", value: selected ? selected.value : "" } });
                        }}
                        styles={CSSForSelectModern}
                    />
                    {errors.gender && <span className="error-message">{errors.gender}</span>}
                </div>

                <div className="form-field">
                    <label><FileText size={14} /> Qualification</label>
                    <input type="text" maxLength={100} name="qualification" placeholder="Example-B.Tech" onChange={handleInputChange} value={formData.qualification} className={errors.qualification ? "error" : inputState.qualification ? "success" : ""} />
                    {errors.qualification && <span className="error-message">{errors.qualification}</span>}
                </div>

                <div className="form-field">
                    <label><Users size={14} /> Marital Status</label>
                    <Select
                        options={maritalStatusOptions}
                        isSearchable placeholder="Select Marital Status"
                        value={maritalStatusOptions.find(opt => opt.value === formData.married_status)}
                        onChange={(selected) => {
                            setFormData(prev => ({ ...prev, married_status: selected ? selected.value : "" }));
                            setInputState(prev => ({ ...prev, married_status: selected ? "green" : "" }));
                        }}
                        isClearable styles={CSSForSelectModern}
                    />
                </div>

                <div className="form-field">
                    <label><User size={14} /> Emergency Person Name</label>
                    <input type="text" maxLength={100} name="emergency_person_name" placeholder="Example-Jhon Doe" onChange={handleInputChange} value={formData.emergency_person_name} className={errors.emergency_person_name ? "error" : inputState.emergency_person_name ? "success" : ""} />
                    {errors.emergency_person_name && <span className="error-message">{errors.emergency_person_name}</span>}
                </div>

                <div className="form-field">
                    <label><Phone size={14} /> Emergency Phone Number</label>
                    <input type="number" maxLength={10} min={0} name="emergency_phone_number" placeholder="Example-785236589" onChange={handleInputChange} value={formData.emergency_phone_number} className={errors.emergency_phone_number ? "error" : inputState.emergency_phone_number ? "success" : ""} />
                    {errors.emergency_phone_number && <span className="error-message">{errors.emergency_phone_number}</span>}
                </div>

                <div className="form-field">
                    <label><Users size={14} /> Relation With {formData.name}</label>
                    <Select
                        options={relationshipOptions}
                        isSearchable placeholder="Select Relationship"
                        value={relationshipOptions.find(opt => opt.value === formData.emergency_relation)}
                        onChange={(selected) => handleInputChange({ target: { name: "emergency_relation", value: selected ? selected.value : "" } })}
                        styles={CSSForSelectModern}
                    />
                    {errors.emergency_relation && <span className="error-message">{errors.emergency_relation}</span>}
                </div>

                <div className="form-field">
                    <label><CreditCard size={14} /> Aadhaar Card No.<span className="required">*</span></label>
                    <input type="number" maxLength={100} min={0} name="aadhaar" placeholder="Example-7524 7852 9523" onChange={handleInputChange} value={formData.aadhaar} className={errors.aadhaar ? "error" : inputState.aadhaar ? "success" : ""} />
                    {errors.aadhaar && <span className="error-message">{errors.aadhaar}</span>}
                </div>

                <div className="form-field">
                    <label><Hash size={14} /> UAN Number</label>
                    <input type="text" maxLength={100} name="uan" placeholder="Example-100456789012" onChange={handleInputChange} value={formData.uan} className={errors.uan ? "error" : inputState.uan ? "success" : ""} />
                    {errors.uan && <span className="error-message">{errors.uan}</span>}
                </div>

                <div className="form-field">
                    <label><CreditCard size={14} /> PAN No. {requireFields && <span className="required">*</span>}</label>
                    <input type="text" name="pan" placeholder="ABCDE1234F" maxLength={10} value={formData.pan} onChange={handleInputChange} className={errors.pan ? "error" : inputState.pan ? "success" : ""} />
                    {errors.pan && <span className="error-message">{errors.pan}</span>}
                </div>

                <div className="form-field full-width">
                    <label><MapPin size={14} /> Current Address</label>
                    <textarea name="current_address" maxLength={300} placeholder="Example-Delhi" onChange={handleInputChange} value={formData.current_address} rows={2} className={errors.current_address ? "error" : inputState.current_address ? "success" : ""} />
                    {errors.current_address && <span className="error-message">{errors.current_address}</span>}
                </div>

                <div className="form-field full-width">
                    <label><MapPin size={14} /> Emergency Address</label>
                    <textarea name="emergency_address" maxLength={300} placeholder="Example-Delhi" onChange={handleInputChange} value={formData.emergency_address} rows={2} className={errors.emergency_address ? "error" : inputState.emergency_address ? "success" : ""} />
                    {errors.emergency_address && <span className="error-message">{errors.emergency_address}</span>}
                </div>
            </div>

            <div className="section-navigation-buttons dual-buttons">
                <div className="nav-buttons-wrapper">
                    <button type="button" className="btn-prev" onClick={goToPreviousTab}>
                        <ChevronRight size={16} className="rotate-left" /> Previous
                    </button>
                    {tabValidationAttempted[activeSection] && hasCurrentTabErrors() && (
                        <div className="tab-error-message">
                            <AlertCircle size={14} />
                            <span>{getCurrentTabErrorMessage()}</span>
                        </div>
                    )}
                    <button type="button" className="btn-next" onClick={goToNextTab} disabled={tabHasMissingFields('personal')}>
                        Next: Bank Details <ChevronRight size={16} />
                    </button>
                </div>
            </div>
        </div>
    );

    const renderBankSection = () => (
        <div className="form-section">
            <div className="section-title">
                <CreditCard size={18} />
                <h4>Bank Details</h4>
            </div>
            <div className="form-grid">
                <div className="form-field">
                    <label><Hash size={14} /> Account Number {requireFields && <span className="required">*</span>}</label>
                    <input type="number" min={0} name="account_number" placeholder="Example-75206516316854" onChange={handleInputChange} value={formData.account_number} className={errors.account_number ? "error" : inputState.account_number ? "success" : ""} />
                    {errors.account_number && <span className="error-message">{errors.account_number}</span>}
                </div>

                <div className="form-field">
                    <label><Hash size={14} /> IFSC code {requireFields && <span className="required">*</span>}</label>
                    <input type="text" maxLength={15} name="ifsc_code" placeholder="Example-ABCIN0000691" onChange={handleInputChange} value={formData.ifsc_code} className={errors.ifsc_code ? "error" : inputState.ifsc_code ? "success" : ""} />
                    {errors.ifsc_code && <span className="error-message">{errors.ifsc_code}</span>}
                </div>

                <div className="form-field">
                    <label><User size={14} /> Account Holder Name {requireFields && <span className="required">*</span>}</label>
                    <input type="text" maxLength={60} name="account_holder_name" placeholder="Example-ABC" onChange={handleInputChange} value={formData.account_holder_name} className={errors.account_holder_name ? "error" : inputState.account_holder_name ? "success" : ""} />
                    {errors.account_holder_name && <span className="error-message">{errors.account_holder_name}</span>}
                </div>

                <div className="form-field">
                    <label><Building2 size={14} /> Bank Name {requireFields && <span className="required">*</span>}</label>
                    <input type="text" maxLength={60} name="bank_name" placeholder="Example-ABC Bank" onChange={handleInputChange} value={formData.bank_name} className={errors.bank_name ? "error" : inputState.bank_name ? "success" : ""} />
                    {errors.bank_name && <span className="error-message">{errors.bank_name}</span>}
                </div>

                <div className="form-field full-width">
                    <label><MapPin size={14} /> Bank Address</label>
                    <textarea maxLength={100} name="branch_address" placeholder="Example-Delhi" onChange={handleInputChange} value={formData.branch_address} rows={2} className={errors.branch_address ? "error" : inputState.branch_address ? "success" : ""} />
                    {errors.branch_address && <span className="error-message">{errors.branch_address}</span>}
                </div>
            </div>

            <div className="form-actions">
                <button type="button" className="btn-prev" onClick={goToPreviousTab}>
                    <ChevronRight size={16} className="rotate-left" /> Previous: Personal Details
                </button>
                <div className="submit-buttons">
                    <button type="button" className="btn-secondaryy" onClick={handleClose}>
                        Cancel
                    </button>
                    <button type="submit" className="btn-primaryy" disabled={loading || tabHasMissingFields('bank')}>
                        {loading ? <Loader size={18} className="spinning" /> : <Save size={18} />}
                        <span>{loading ? 'Updating...' : 'Update'}</span>
                    </button>
                </div>
            </div>

            <p className="error-message" style={{ marginTop: '12px' }}>
                {!isSubmitted && Object.keys(errors).length > 0 && (
                    Object.keys(errors).length > 5 ? (
                        <span>Please fill all mandatory fields!</span>
                    ) : (
                        Object.keys(errors).map((field) => fieldLabels[field] || field).join(', ') + ' are required!'
                    )
                )}
            </p>

            {tabValidationAttempted[activeSection] && hasCurrentTabErrors() && (
                <div className="tab-error-message" style={{ marginTop: '12px' }}>
                    <AlertCircle size={14} />
                    <span>{getCurrentTabErrorMessage()}</span>
                </div>
            )}
        </div>
    );

    const renderCurrentSection = () => {
        switch (activeSection) {
            case 'employee': return renderEmployeeSection();
            case 'personal': return renderPersonalSection();
            case 'bank': return renderBankSection();
            default: return renderEmployeeSection();
        }
    };

    return (
        <>
            <button className="model-edit-button-modern" style={{ background: "#00000075" }} onClick={handleOpenClick}>
                <UpdatePencil modernButton="true" />
            </button>
            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Update" />
            ) : (
                <>
                    {role === "Admin" && (
                        <Modal show={showrequired} onHide={handleHideRequiredFieldModal} dialogClassName="modern-required-modal" centered>
                            <Modal.Header className="modern-modal-header requirement-modal-header">
                                <div className="header-content">
                                    <div className="requirement-header-icon">
                                        <ShieldCheck size={22} />
                                    </div>
                                    <div>
                                        <Modal.Title>Form Requirement Mode</Modal.Title>
                                        <p className="header-subtitle" style={{ color: "white" }}>Choose how strict the form validation should be</p>
                                    </div>
                                </div>
                                <button className="close-btn" onClick={handleHideRequiredFieldModal}>
                                    <X size={20} />
                                </button>
                            </Modal.Header>

                            <Modal.Body className="modern-modal-body requirement-modal-body">
                                <div className="requirement-mode-grid">
                                    <div
                                        className={`requirement-mode-card ${!requireFields ? 'selected' : ''}`}
                                        onClick={() => setRequiredFields(false)}
                                    >
                                        <div className="requirement-mode-icon basic">
                                            <Zap size={20} />
                                        </div>
                                        <h5>Basic Fields Only</h5>
                                        <p>Only essential fields required — code, name, role, email, mobile, aadhaar.</p>
                                        <div className="requirement-mode-check">
                                            {!requireFields && <Check size={14} />}
                                        </div>
                                    </div>

                                    <div
                                        className={`requirement-mode-card ${requireFields ? 'selected' : ''}`}
                                        onClick={() => setRequiredFields(true)}
                                    >
                                        <div className="requirement-mode-icon strict">
                                            <ShieldCheck size={20} />
                                        </div>
                                        <h5>All Fields Required</h5>
                                        <p>Employee, personal and bank details all become mandatory before submission.</p>
                                        <div className="requirement-mode-check">
                                            {requireFields && <Check size={14} />}
                                        </div>
                                    </div>
                                </div>

                                <div className="requirement-mode-summary">
                                    <Info size={14} />
                                    <span>
                                        Currently in <strong>{requireFields ? "Strict" : "Basic"}</strong> mode — you can change this anytime before submitting.
                                    </span>
                                </div>

                                <div className="submit-buttons" style={{ justifyContent: 'flex-end', marginTop: '20px' }}>
                                    <button type="button" className="btn-secondaryy" onClick={handleHideRequiredFieldModal}>Cancel</button>
                                    <button
                                        type="button"
                                        className="btn-primaryy"
                                        onClick={() => { handleHideRequiredFieldModal(); handleShow(); }}
                                    >
                                        Continue <ChevronRight size={16} />
                                    </button>
                                </div>
                            </Modal.Body>
                        </Modal>
                    )}

                    <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="xl">
                        <Modal.Header className="modern-modal-header">
                            <div className="header-content">
                                <div style={{ color: "white" }}>
                                    <Users size={24} />
                                </div>
                                <div>
                                    <Modal.Title>Update Site Employee</Modal.Title>
                                    <p className="header-subtitle" style={{ color: "white" }}>Update the details of this employee</p>
                                </div>
                            </div>
                            <button className="close-btn" onClick={handleClose}>
                                <X size={20} />
                            </button>
                        </Modal.Header>

                        <Modal.Body className="modern-modal-body">
                            <div className="section-navigation">
                                {sections.map((section) => {
                                    const Icon = section.icon;
                                    const hasMissing = tabHasMissingFields(section.id);
                                    return (
                                        <button
                                            key={section.id}
                                            className={`section-nav-btn ${activeSection === section.id ? 'active' : ''} ${hasMissing && section.id !== activeSection ? 'has-warning' : ''}`}
                                            onClick={() => {
                                                if (activeSection !== section.id) {
                                                    if (validateCurrentTab()) {
                                                        setActiveSection(section.id);
                                                    } else {
                                                        const formContainer = document.querySelector('.modern-modal-body');
                                                        if (formContainer) formContainer.scrollTop = 0;
                                                    }
                                                }
                                            }}
                                        >
                                            <Icon size={16} />
                                            <span>{section.label}</span>
                                            {hasMissing && section.id !== activeSection && (
                                                <div className="warning-dot" title="Missing required fields" />
                                            )}
                                            {activeSection === section.id && <ChevronRight size={14} className="nav-arrow" />}
                                        </button>
                                    );
                                })}
                            </div>

                            <form onSubmit={handleFormSubmit} className="project-form">
                                {buffer ? (
                                    <div className="loader-container">
                                        <Loader size={40} className="spinning" />
                                        <p>Loading data...</p>
                                    </div>
                                ) : (
                                    renderCurrentSection()
                                )}
                            </form>

                            <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                        </Modal.Body>
                    </Modal>
                </>
            )}

        </>
    );
};

const UpdateSiteEmployeeDesignation = ({ i, getSiteDepartmentEmployees, siteSubCompanyList, projectId }) => {

    const companyId = sessionStorage.getItem("sitemanagement_project_sub_company");




    const [show, setShow] = useState(false)
    const [buffer, setBuffering] = useState(true); //buffering logic
    const siteRole = sessionStorage.getItem("sitemanagement_role");
    const [isSubmitted, setIsSubmitted] = useState(false);

    const [designationList, setDesignationList] = useState([]);

    const handleClose = () => setShow(false);
    const handleShow = async () => {
        // setFormData(i);
        getUserDetails()
        setShow(true);
    };



    const getUserDetails = async () => {
        setBuffering(true); // Start Buffering
        try {
            // Encode emp_code to handle special characters like '/' 
            // ! Do not remove below line
            const encodedEmpCode = encodeURIComponent(i.emp_code);
            const res = await axios.get(`${BASE_URL}/siteproject/employeebyempcode/${encodedEmpCode}/`);
            setFormData(res.data);
        } catch (err) {
            handleErrorToast(err);
        } finally {
            setBuffering(false); // End Buffering
        }
    };

    const [formData, setFormData] = useState({
        emp_code: "",
        name: "",
        email: "",
        mobile: 0,
        designation: "",
        professional_type: "",
        current_address: "",
        joining_date: "",
        employee_status: "",
        division: companyId ? companyId : null,
        emp_role: null,
        dob: "",
        gender: "",
        married_status: "",
        qualification: "",
        emergency_person_name: "",
        emergency_phone_number: "",
        emergency_relation: "",
        emergency_address: "",
        account_number: "",
        ifsc_code: "",
        bank_name: "",
        branch_address: "",
        aadhaar: "",
        uan: "",
        replacement_deduction: "",

    });

    useEffect(() => {
        if (formData.division != null && formData.division !== undefined && show === true) {
            const getDesignation = async () => {
                try {
                    const response = await axios.get(`${BASE_URL}/siteproject/designationfilterbysubcompany/${formData.division}/`);
                    setDesignationList(response.data);
                } catch (error) {
                    // Handle error if needed
                    console.error("Error fetching designations:", error);
                }
            };

            // Call the function
            getDesignation();
        }
    }, [formData.division, show]);


    const [siteProjectList, setProjectList] = useState([]);


    console.log("suub ccompany")

    console.log(formData.division)


    const getproject = async () => {

        console.log("hllo")
        try {
            // const response = await axios.get(`${BASE_URL}/siteproject/projectfilterbysubcompany/${subCompany}`);
            const response = await axios.get(`${BASE_URL}/siteproject/projectfilterbysubcompany/${formData.division}/`
            );
            const sortedList = sortProjects(response.data, "site_prcode");
            // setProjectList(response.data)
            setProjectList(sortedList)

        } catch (error) {
            alert(error);
        }
    }


    console.log(siteProjectList)

    useEffect(() => {
        getproject()
    }, [formData.division])






    // !  ************** Validation start **************  ! //

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = [

        ];

        // if (siteRole !== "SiteManagement") {
        //     requiredFields.push("project", 'employee_status', "division", 'boq_salary', 'actual_salary', 'emp_role');
        // }
        // requiredFields.forEach((field) => {
        //     if (!formData[field]) {
        //         newErrors[field] = ` ${field.charAt(0).toUpperCase() + field.slice(1)
        //             } is required !`;
        //     }
        // });


        setErrors(newErrors);
        setIsSubmitted(true);

        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        setInputState({
            ...inputState,
            [name]: value.trim() ? "green" : "",
        });

        setFormData({
            ...formData,
            [name]: value,
        });
    };


    // ?  ************** Validation End **************  ! //
    const [loading, setLoading] = useState(false); //loading logic


    const handleFormSubmit = async (e) => {
        e.preventDefault();

        if (validateForm()) {
            setLoading(true); //loading logic
            const loadingToastId = toast.loading("Loading: Please wait..."); //toast Logic

            try {
                let res = await axios.put(`${BASE_URL}/siteproject/employee/${i.id}/`, formData)

                if (res.status === 200) {
                    await getSiteDepartmentEmployees()
                    handleClose()
                }
            } catch (err) {
                //toast Logic
                handleErrorToast(err)
            } finally {
                setLoading(false); //loading logic
                toast.dismiss(loadingToastId);
            }
        }
    };



    return (
        <>

            <button className="model-edit-button" onClick={handleShow}>
                <UpdatePencil />
            </button>

            <Modal show={show} onHide={handleClose} dialogClassName="ourcompany-model">
                <Modal.Header closeButton>
                    <Modal.Title>Update Site Employee</Modal.Title>
                </Modal.Header>
                <Modal.Body>

                    {/*  ************** timeline Seaction  ************** */}

                    <>
                        <form className="register-form">
                            <div className="form-flex-wrap">
                                <label style={{ fontSize: '18px', fontWeight: 500, borderBottom: '3px solid #2576bc', marginBottom: '8px' }}>Personal Details</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
                                    <div title="Name" className="flex-column">
                                        <label htmlFor="name" className="form-labels  font-weight500    font-size-subheading">
                                            Name<span className="required">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            maxLength={100}
                                            id="name"
                                            name="name"
                                            placeholder="Name"
                                            onChange={handleInputChange}
                                            value={formData.name}
                                            className={`form-input ${errors.name
                                                ? "error"
                                                : inputState.name
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.name && (
                                            <span className="error-message font-size-text ">{errors.name}</span>
                                        )}
                                    </div>

                                    <div title="Email" className="flex-column">
                                        <label htmlFor="email" className="form-labels  font-weight500    font-size-subheading">
                                            Email<span className="required">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            id="email"
                                            name="email"
                                            placeholder="Demo@gmail.com"
                                            onChange={handleInputChange}
                                            value={formData.email}
                                            onInput={(e) => {
                                                e.target.value = e.target.value.toLowerCase().trim(); // Convert input to lowercase
                                            }}
                                            className={`form-input ${errors.email
                                                ? "error"
                                                : inputState.email
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.email && (
                                            <span className="error-message font-size-text ">{errors.email}</span>
                                        )}
                                    </div>

                                    <div title="Mobile No." className="flex-column">
                                        <label htmlFor="mobile" className="form-labels  font-weight500    font-size-subheading">
                                            Mobile No.<span className="required">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            min={0}
                                            maxLength={10}
                                            id="mobile"
                                            name="mobile"
                                            placeholder="Mobile Number"
                                            onChange={handleInputChange}
                                            value={formData.mobile}
                                            className={`form-input ${errors.mobile
                                                ? "error"
                                                : inputState.mobile
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.mobile && (
                                            <span className="error-message font-size-text ">{errors.mobile}</span>
                                        )}
                                    </div>

                                    <div title="dob" className="flex-column">
                                        <label htmlFor="dob" className="form-labels  font-weight500    font-size-subheading">
                                            DOB
                                        </label>
                                        <input
                                            type="date"
                                            id="dob"
                                            name="dob"
                                            onChange={handleInputChange}
                                            value={formData.dob}
                                            className={`form-input ${errors.dob ? "error" : inputState.dob ? "success" : ""
                                                }`}
                                        />
                                        {errors.dob && <span className="error-message font-size-text ">{errors.dob}</span>}
                                    </div>

                                    <div title="Gender" className="flex-column form-group-select">
                                        <label htmlFor="gender" className="form-labels font-weight500    font-size-subheading">
                                            Gender<span className="required">*</span>
                                        </label>
                                        <br />
                                        <select
                                            id="gender"
                                            name="gender"
                                            value={formData.gender}
                                            onChange={handleInputChange}
                                            className={`form-input form-group-selection ${errors.gender ? "error" : inputState.gender ? "success" : ""
                                                }`}
                                        >
                                            <option value="">Select Gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                            <option value="other">Other</option>
                                        </select>

                                        <div>
                                            {errors.gender && (
                                                <span className="error-message font-size-text ">{errors.gender}</span>
                                            )}
                                        </div>
                                    </div>

                                    <div title="qualification" className="flex-column">
                                        <label htmlFor="qualification" className="form-labels  font-weight500    font-size-subheading">
                                            Qualification
                                        </label>
                                        <input
                                            type="text"
                                            maxLength={100}
                                            id="qualification"
                                            name="qualification"
                                            placeholder="qualification"
                                            onChange={handleInputChange}
                                            value={formData.qualification}
                                            className={`form-input ${errors.qualification
                                                ? "error"
                                                : inputState.qualification
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.qualification && (
                                            <span className="error-message font-size-text ">{errors.qualification}</span>
                                        )}
                                    </div>

                                    <div className="flex-column form-group-select">
                                        <label htmlFor="married_status" className="form-labels font-weight500    font-size-subheading">
                                            Marital Status
                                        </label>
                                        <br />
                                        <select
                                            id="married_status"
                                            name="married_status"
                                            value={formData.married_status}
                                            onChange={handleInputChange}
                                            className={`form-input form-group-selection ${errors.married_status ? "error" : inputState.married_status ? "success" : ""
                                                }`}
                                        >
                                            <option value="">Select Status</option>
                                            <option value="Single">Single</option>
                                            <option value="Married">Married</option>
                                            <option value="Divorced">Divorced</option>
                                            <option value="Widowed">Widowed</option>
                                            <option value="Separated">Separated</option>
                                        </select>

                                        <div>
                                            {errors.married_status && (
                                                <span className="error-message font-size-text ">{errors.married_status}</span>
                                            )}
                                        </div>
                                    </div>

                                    <div title="emergency_person_name" className="flex-column">
                                        <label htmlFor="emergency_person_name" className="form-labels  font-weight500    font-size-subheading">
                                            Emergency Person Name
                                        </label>
                                        <input
                                            type="text"
                                            maxLength={100}
                                            id="emergency_person_name"
                                            name="emergency_person_name"
                                            placeholder="P"
                                            onChange={handleInputChange}
                                            value={formData.emergency_person_name}
                                            className={`form-input ${errors.emergency_person_name
                                                ? "error"
                                                : inputState.emergency_person_name
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.emergency_person_name && (
                                            <span className="error-message font-size-text ">{errors.emergency_person_name}</span>
                                        )}
                                    </div>

                                    <div title="emergency_phone_number" className="flex-column">
                                        <label htmlFor="emergency_phone_number" className="form-labels  font-weight500    font-size-subheading">
                                            Emergency Phone Number
                                        </label>
                                        <input
                                            type="text"
                                            maxLength={100}
                                            id="emergency_phone_number"
                                            name="emergency_phone_number"
                                            placeholder="Emergency Phone Number"
                                            onChange={handleInputChange}
                                            value={formData.emergency_phone_number}
                                            className={`form-input ${errors.emergency_phone_number
                                                ? "error"
                                                : inputState.emergency_phone_number
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.emergency_phone_number && (
                                            <span className="error-message font-size-text ">{errors.emergency_phone_number}</span>
                                        )}
                                    </div>

                                    <div title="emergency_relation" className="flex-column">
                                        <label htmlFor="emergency_relation" className="form-labels  font-weight500    font-size-subheading">
                                            Relative
                                        </label>
                                        <input
                                            type="text"
                                            maxLength={100}
                                            id="emergency_relation"
                                            name="emergency_relation"
                                            placeholder=" Relative Name"
                                            onChange={handleInputChange}
                                            value={formData.emergency_relation}
                                            className={`form-input ${errors.emergency_relation
                                                ? "error"
                                                : inputState.emergency_relation
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.emergency_relation && (
                                            <span className="error-message font-size-text ">{errors.emergency_relation}</span>
                                        )}
                                    </div>

                                    <div title="Addhar card" className="flex-column">
                                        <label htmlFor="aadhaar" className="form-labels  font-weight500    font-size-subheading">
                                            Aadhaar Number
                                        </label>
                                        <input
                                            type="text"
                                            maxLength={100}
                                            id="aadhaar"
                                            name="aadhaar"
                                            placeholder="Aadhaar card"
                                            onChange={handleInputChange}
                                            value={formData.aadhaar}
                                            className={`form-input ${errors.aadhaar
                                                ? "error"
                                                : inputState.aadhaar
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.aadhaar && (
                                            <span className="error-message font-size-text ">{errors.aadhaar}</span>
                                        )}
                                    </div>

                                    <div title="uan" className="flex-column">
                                        <label htmlFor="uan" className="form-labels  font-weight500    font-size-subheading">
                                            UAN
                                        </label>
                                        <input
                                            type="text"
                                            maxLength={100}
                                            id="uan"
                                            name="uan"
                                            placeholder="uan"
                                            onChange={handleInputChange}
                                            value={formData.uan}
                                            className={`form-input ${errors.uan
                                                ? "error"
                                                : inputState.uan
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.uan && (
                                            <span className="error-message font-size-text ">{errors.uan}</span>
                                        )}
                                    </div>

                                    <div title="pan card" className="flex-column">
                                        <label htmlFor="pan" className="form-labels  font-weight500    font-size-subheading">
                                            PAN Number
                                        </label>
                                        <input
                                            type="text"
                                            maxLength={100}
                                            id="pan"
                                            name="pan"
                                            placeholder="pan card"
                                            onChange={handleInputChange}
                                            value={formData.pan}
                                            className={`form-input ${errors.pan
                                                ? "error"
                                                : inputState.pan
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.pan && (
                                            <span className="error-message font-size-text ">{errors.pan}</span>
                                        )}
                                    </div>

                                    <div title="Current Address" className="flex-column">
                                        <label htmlFor="current_address" className="form-labels  font-weight500    font-size-subheading">
                                            Current Address<span className="required">*</span>
                                        </label>
                                        <textarea
                                            type="text"
                                            id="current_address"
                                            name="current_address"
                                            maxLength={300}
                                            placeholder="Current Address"
                                            onChange={handleInputChange}
                                            value={formData.current_address}
                                            className={`form-input-textarea   font-weight400  font-size-subheading ${errors.current_address
                                                ? "error"
                                                : inputState.current_address
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.current_address && (
                                            <span className="error-message font-size-text ">{errors.current_address}</span>
                                        )}
                                    </div>

                                    <div title="Emergency Address" className="flex-column">
                                        <label htmlFor="emergency_address" className="form-labels  font-weight500    font-size-subheading">
                                            Emergency Address<span className="required">*</span>
                                        </label>
                                        <textarea
                                            type="text"
                                            id="emergency_address"
                                            name="emergency_address"
                                            maxLength={300}
                                            placeholder="Emergency Address"
                                            onChange={handleInputChange}
                                            value={formData.emergency_address}
                                            className={`form-input-textarea   font-weight400  font-size-subheading ${errors.emergency_address
                                                ? "error"
                                                : inputState.emergency_address
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.emergency_address && (
                                            <span className="error-message font-size-text ">{errors.emergency_address}</span>
                                        )}
                                    </div>
                                </div>

                                <label style={{ fontSize: '18px', fontWeight: 500, borderBottom: '3px solid #2576bc', marginBottom: '8px' }}>Bank Details</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
                                    <div title="account number" className="flex-column">
                                        <label htmlFor="account_number" className="form-labels  font-weight500    font-size-subheading">
                                            Account Number
                                        </label>
                                        <input
                                            type="text"
                                            // maxLength={100}
                                            id="account_number"
                                            name="account_number"
                                            placeholder="Account number"
                                            onChange={handleInputChange}
                                            value={formData.account_number}
                                            className={`form-input ${errors.account_number
                                                ? "error"
                                                : inputState.account_number
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.account_number && (
                                            <span className="error-message font-size-text ">{errors.account_number}</span>
                                        )}
                                    </div>

                                    <div title="IFSC code" className="flex-column">
                                        <label htmlFor="ifsc_code" className="form-labels  font-weight500    font-size-subheading">
                                            IFSC code
                                        </label>
                                        <input
                                            type="text"
                                            maxLength={15}
                                            id="ifsc_code"
                                            name="ifsc_code"
                                            placeholder="ifsc code"
                                            onChange={handleInputChange}
                                            value={formData.ifsc_code}
                                            className={`form-input ${errors.ifsc_code
                                                ? "error"
                                                : inputState.ifsc_code
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.ifsc_code && (
                                            <span className="error-message font-size-text ">{errors.ifsc_code}</span>
                                        )}
                                    </div>

                                    <div title="Bank Name" className="flex-column">
                                        <label htmlFor="bank_name" className="form-labels  font-weight500    font-size-subheading">
                                            Bank Name
                                        </label>
                                        <input
                                            type="text"
                                            maxLength={60}
                                            id="bank_name"
                                            name="bank_name"
                                            placeholder="Bank Name"
                                            onChange={handleInputChange}
                                            value={formData.bank_name}
                                            className={`form-input ${errors.bank_name
                                                ? "error"
                                                : inputState.bank_name
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.bank_name && (
                                            <span className="error-message font-size-text ">{errors.bank_name}</span>
                                        )}
                                    </div>

                                    <div title="Bank address" className="flex-column">
                                        <label htmlFor="branch_address" className="form-labels  font-weight500    font-size-subheading">
                                            Bank Address
                                        </label>
                                        <input
                                            type="text"
                                            maxLength={100}
                                            id="branch_address"
                                            name="branch_address"
                                            placeholder="Bank address"
                                            onChange={handleInputChange}
                                            value={formData.branch_address}
                                            className={`form-input ${errors.branch_address
                                                ? "error"
                                                : inputState.branch_address
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.branch_address && (
                                            <span className="error-message font-size-text ">{errors.branch_address}</span>
                                        )}
                                    </div>
                                </div>

                                <label style={{ fontSize: '18px', fontWeight: 500, borderBottom: '3px solid #2576bc', marginBottom: '8px' }}>Employement Details</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
                                    <div title="Emp Code" className="flex-column">
                                        <label htmlFor="emp_code" className="form-labels  font-weight500    font-size-subheading">
                                            Emp Code<span className="required">*</span>
                                        </label>
                                        <input
                                            maxLength={20}
                                            type="text"
                                            id="emp_code"
                                            name="emp_code"
                                            placeholder="Emp Code"
                                            onChange={handleInputChange}
                                            value={formData.emp_code}
                                            className={`form-input ${errors.emp_code
                                                ? "error"
                                                : inputState.emp_code
                                                    ? "success"
                                                    : ""
                                                }`}
                                        />
                                        {errors.emp_code && (
                                            <span className="error-message font-size-text ">{errors.emp_code}</span>
                                        )}
                                    </div>
                                    {siteRole === "SiteManagement" ? "" :
                                        <>
                                            <div title="Site Sub Company" className="flex-column form-group-selectt">
                                                <label htmlFor="division" className="form-labels  font-weight500    font-size-subheading">
                                                    Division<span className="required">*</span>
                                                </label>
                                                <br />
                                                <select
                                                    id="division"
                                                    name="division"
                                                    value={formData.division}
                                                    onChange={handleInputChange}
                                                    className={`form-input form-group-selection ${errors.division ? "error" : inputState.division ? "success" : ""
                                                        }`}
                                                >
                                                    <option value="">Select a Site SubCompany</option>
                                                    {siteSubCompanyList?.sort((a, b) => a?.title?.localeCompare(b.title, 'en', { ignorePunctuation: true })).map((i, index) => (<>
                                                        <option value={i.id}>{i.title}</option>
                                                    </>))
                                                    }
                                                </select>

                                                <div>
                                                    {errors.division && (
                                                        <span className="error-message font-size-text ">{errors.division}</span>
                                                    )}
                                                </div>
                                            </div>



                                            <div title="Employee Status" className="flex-column form-group-selectt">
                                                <label htmlFor="employee_status" className="form-labels  font-weight500    font-size-subheading">
                                                    Employee Status<span className="required">*</span>
                                                </label>
                                                <br />
                                                <select
                                                    id="employee_status"
                                                    name="employee_status"
                                                    value={formData.employee_status}
                                                    onChange={handleInputChange}
                                                    className={`form-input form-group-selection ${errors.employee_status ? "error" : inputState.employee_status ? "success" : ""
                                                        }`}
                                                >
                                                    <option value="">Select Option</option>
                                                    <option value="active">Active</option>
                                                    <option value="inactive">In Active</option>
                                                </select>

                                                <div>
                                                    {errors.employee_status && (
                                                        <span className="error-message font-size-text ">{errors.employee_status}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </>
                                    }

                                    {siteRole === "SiteManageent" ? "" :
                                        <div title="Replacement Deduction " className="flex-column">
                                            <label htmlFor="replacement_deduction" className="form-labels  font-weight500    font-size-subheading">
                                                Replacement Deduction
                                            </label>
                                            <input
                                                type="text"
                                                min={0}
                                                maxLength={10}
                                                id="replacement_deduction"
                                                name="replacement_deduction"
                                                placeholder=" Replacement Deduction"
                                                onChange={handleInputChange}
                                                value={formData.replacement_deduction}
                                                className={`form-input ${errors.replacement_deduction
                                                    ? "error"
                                                    : inputState.replacement_deduction
                                                        ? "success"
                                                        : ""
                                                    }`}
                                            />
                                            {errors.replacement_deduction && (
                                                <span className="error-message font-size-text ">{errors.replacement_deduction}</span>
                                            )}
                                        </div>
                                    }

                                    <div title="Site Designation" className="flex-column form-group-selectt">
                                        <label htmlFor="designation" className="form-labels  font-weight500    font-size-subheading">
                                            Designation<span className="required">*</span>
                                        </label>
                                        <br />
                                        <select
                                            id="designation"
                                            name="designation"
                                            value={formData.designation}
                                            onChange={handleInputChange}
                                            className={`form-input form-group-selection ${errors.designation ? "error" : inputState.designation ? "success" : ""
                                                }`}
                                        >
                                            {designationList.length === 0 ? <option value="">Add Designation First</option> : <option value="">Select Designation</option>}
                                            {designationList?.sort((a, b) => a.title?.localeCompare(b.title, 'en', { ignorePunctuation: true })).map((i, index) => (<>
                                                <option value={i.id}>{i.title}</option>
                                            </>))
                                            }
                                        </select>


                                        <div>
                                            {errors.designation && (
                                                <span className="error-message font-size-text ">{errors.designation}</span>
                                            )}
                                        </div>
                                    </div>
                                    {siteRole === "SiteManagement" ? "" :
                                        <div title="Employee Role" className="flex-column form-group-selectt">
                                            <label htmlFor="emp_role" className="form-labels  font-weight500    font-size-subheading">
                                                Employee Role<span className="required">*</span>
                                            </label>
                                            <br />
                                            <select
                                                id="emp_role"
                                                name="emp_role"
                                                value={formData.emp_role}
                                                onChange={handleInputChange}
                                                className={`form-input form-group-selection ${errors.emp_role ? "error" : inputState.emp_role ? "success" : ""
                                                    }`}
                                            >
                                                <option value="">Select Employee Role </option>
                                                <option value="Admin">Admin</option>
                                                <option value="AuthorityEngineer">Authority Engineer</option>
                                                <option value="DataEntryOperator">Coordinator</option>
                                                <option value="Account">Account</option>
                                                <option value="Hr">HR</option>
                                                <option value="BD">BD</option>
                                                <option value="Checker">Checker</option>
                                                <option value="SiteManagement">Site Access Account</option>
                                                <option value="Employee">Employee</option>
                                                <option value="null">Site Employee</option>
                                            </select>

                                            <div>
                                                {errors.emp_role && (
                                                    <span className="error-message font-size-text ">{errors.emp_role}</span>
                                                )}
                                            </div>
                                        </div>
                                    }

                                    <div title="Professional Type" className="flex-column form-group-selectt">
                                        <label htmlFor="professional_type" className="form-labels  font-weight500    font-size-subheading">
                                            Employee Type<span className="required">*</span>
                                        </label>
                                        <br />
                                        <select
                                            id="professional_type"
                                            name="professional_type"
                                            value={formData.professional_type}
                                            onChange={handleInputChange}
                                            className={`form-input form-group-selection ${errors.professional_type ? "error" : inputState.professional_type ? "success" : ""
                                                }`}
                                        >
                                            <option value="null">Select a Professional </option>
                                            <option value="Key Professional">Key Professional</option>
                                            <option value="Sub Professional">Sub Professional</option>
                                            <option value="Support Staff">Support Staff</option>
                                            <option value="Ho Staff">Ho Staff</option>
                                        </select>

                                        <div>
                                            {errors.professional_type && (
                                                <span className="error-message font-size-text ">{errors.professional_type}</span>
                                            )}
                                        </div>
                                    </div>


                                    <div title="Joining Date" className="flex-column">
                                        <label htmlFor="joining_date" className="form-labels  font-weight500    font-size-subheading">
                                            Joining Date<span className="required">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            id="joining_date"
                                            name="joining_date"
                                            onChange={handleInputChange}
                                            value={formData.joining_date}
                                            className={`form-input ${errors.joining_date ? "error" : inputState.joining_date ? "success" : ""
                                                }`}
                                        />
                                        {errors.joining_date && <span className="error-message font-size-text ">{errors.joining_date}</span>}
                                    </div>
                                    {siteRole === "SiteManagement" ? "" :
                                        <div title="Site Projects" className="flex-column form-group-selectt">
                                            <label htmlFor="project" className="form-labels  font-weight500    font-size-subheading">
                                                Site Projects<span className="required">*</span>
                                            </label>
                                            <br />
                                            <select
                                                id="project"
                                                name="project"
                                                value={formData.project}
                                                onChange={handleInputChange}
                                                className={`form-input form-group-selection ${errors.project ? "error" : inputState.project ? "success" : ""
                                                    }`}
                                            // disabled={formData.project === "" ? false : true}
                                            >
                                                <option value="">Select a Project</option>
                                                {siteProjectList?.sort((a, b) => a.project_short_name.localeCompare(b.title, 'en', { ignorePunctuation: true })).map((i, index) => (<>
                                                    <option value={i.id}>{i.site_prcode}-{i.project_short_name}</option>
                                                </>))
                                                }
                                            </select>
                                            {formData.project !== "" ? "" : <div className="form-group-selection-arrow">
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="20"
                                                    height="25"
                                                    fill="currentColor"
                                                    class="bi bi-caret-down-fill"
                                                    viewBox="0 0 16 16"
                                                >
                                                    <path d="M7.247 11.14 2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z" />
                                                </svg>
                                            </div>}
                                            <div>
                                                {errors.project && (
                                                    <span className="error-message font-size-text ">{errors.project}</span>
                                                )}
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>

                            <div className="button-models">

                                <button className="model-button-cancel model-button   font-weight500   " onClick={(e) => handleClose()}>
                                    Cancel
                                </button>
                                <button onClick={handleFormSubmit} className="model-button   font-weight500    model-button-submit" disabled={loading} >{/* //loading logic */}
                                    Update
                                </button>
                            </div>

                            <p className="error-message font-size-text">
                                {!isSubmitted && (
                                    Object.keys(errors).length > 5 ? (
                                        <h5 className="text-center"> 'Please fill all mandatory fields!'</h5>

                                    ) : (
                                        Object.keys(errors).map((field) =>
                                            field.charAt(0).toUpperCase() + field.slice(1)
                                        ).join(', ') + ' are required!'
                                    )
                                )}
                            </p>
                        </form>
                    </>

                    <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop={true} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                </Modal.Body>
            </Modal>

        </>
    );
};

const DeleteSiteEmployee = ({ i, getSiteEmployee }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleFormSubmitDelete = async (e) => {
        e.preventDefault();
        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            let res = await axios.delete(`${BASE_URL}/siteproject/employee/${i.id}/`);

            if (res.status === 200) {
                await getSiteEmployee();
                toast.dismiss(loadingToastId);
                toast.success("Deleted Successfully");
                setShow(false);
            } else {
                alert(res);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <button title="Delete Site Employee" className="model-delete-button-modern" style={{ backgroundColor: "#00000075" }} onClick={handleShow}>
                <DeleteDustbin modernButton="true" />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Delete" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-required-modal" centered>
                    <Modal.Header className="modern-modal-header requirement-modal-header">
                        <div className="header-content">
                            <div className="requirement-header-icon">
                                <Trash2 size={22} />
                            </div>
                            <div>
                                <Modal.Title>Delete Employee</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    This action cannot be undone
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body requirement-modal-body">
                        <div className="requirement-mode-summary" style={{ marginBottom: '20px' }}>
                            <AlertCircle size={16} />
                            <span>
                                Are you sure you want to delete <strong>{i.name}</strong>'s data?
                            </span>
                        </div>

                        <div className="submit-buttons" style={{ justifyContent: 'flex-end' }}>
                            <button type="button" className="btn-secondaryy" onClick={handleClose} disabled={loading}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn-primaryy"
                                style={{ background: "#dc3545", borderColor: "#dc3545" }}
                                onClick={handleFormSubmitDelete}
                                disabled={loading}
                            >
                                {loading ? <Loader size={18} className="spinning" /> : <Trash2 size={18} />}
                                <span>{loading ? 'Deleting...' : 'Delete'}</span>
                            </button>
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};
const AddBOQEmployee = ({ projectId, getEmployee, professional_type, designation }) => {

};


// ? *************** Employee Crud Models End ***************



// const EmployeeDetailCard = ({ data, companyList, getSiteEmployee, siteDepartmentList, siteProjectList, siteSubCompanyList }) => {
//     const siteRole = sessionStorage.getItem("sitemanagement_role");
//     const currentUrl = window.location.href;
//     const urlParts = currentUrl.split("/");
//     const navigate = useNavigate()
//     const Viewdetails = (emp_code) => {
//         navigate(`/${urlParts[3]}/siteemployeedetail/${emp_code}/`)
//     }

//     return (
//         <div className='employee-cards  table-box' style={{ maxHeight: '73vh' }} >

//             {data?.sort((a, b) => {
//                 const order = { "Key Professional": 1, "Sub Professional": 2, "Support Staff": 3 };
//                 return (order[a.professional_type] || 4) - (order[b.professional_type] || 4);
//             }).map((e, index) => {

//                 return (
//                     <div className='employee-card'>
//                         <div className='employee-card-a'>



//                             <div className='employee-card-image'>
//                                 {/* <div className='employee-card-image-background'>
//                     <img src={e.profilepic != "" ? `${IMAGE_URL}${e.profilepic}` : 'https://images.unsplash.com/photo-1511367461989-f85a21fda167?q=80&w=1931&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'} alt="card-pic" className="employee-card-imageee" />
//                   </div> */}
//                                 <div className='employee-card-icons'>
//                                     <div className='employee-card-name font-weight500   font-size-subheading'>{e.name}</div>
//                                     <div className='employee-card-designation font-weight400   font-size-subheading'>{e.designation_name}</div>
//                                     <div className="employee-card-name font-weight500   font-size-subheading">Emp Code: {e.emp_code}</div>


//                                 </div>

//                             </div>
//                             {siteRole === "SiteManagement" ?
//                                 <div title="Edit" className="flex-row">
//                                     <button className='employee-edit' onClick={() => Viewdetails(e.emp_code)} title="View Profile">
//                                         <Tripledot />
//                                     </button>
//                                 </div>
//                                 :
//                                 <div title="Edit" className="flex-row">

//                                     {
//                                         siteRole !== "ViewAccess" &&
//                                         <UpdateSiteEmployee
//                                             i={e}
//                                             getSiteEmployee={getSiteEmployee}
//                                             siteProjectList={siteProjectList}
//                                             siteSubCompanyList={siteSubCompanyList}
//                                             companyList={companyList}
//                                         />
//                                     }
//                                     <button className='employee-edit' onClick={() => Viewdetails(e.emp_code)} title="View Profile">
//                                         <Tripledot />
//                                     </button>

//                                     {(siteRole === "Hr" || "Admin") && siteRole !== "ViewAccess" ?
//                                         <>
//                                             <DeleteSiteEmployee
//                                                 i={e}
//                                                 getSiteEmployee={getSiteEmployee}
//                                             />
//                                         </> : ""}
//                                 </div>
//                             }

//                         </div>
//                         <div className='employee-card-b'>
//                             <div className='employee-card-b-flex'>
//                                 <div className="cart-subdiv-flex">
//                                     <span className='employee-card-b-heading font-weight400 font-size-label'>Project: </span>
//                                     <span className='employee-card-b-text font-weight400 ing font-size-label'>
//                                         {e.multiple_project_names?.length > 0
//                                             ? e.multiple_project_names.map((project, index) => (
//                                                 <React.Fragment key={index}>
//                                                     {project}
//                                                     <br />
//                                                 </React.Fragment>
//                                             ))
//                                             : e.project_short_name}
//                                     </span>
//                                 </div>
//                             </div>

//                             <div className='employee-card-b-flex'>
//                                 <div className="cart-subdiv-flex" ><span className='employee-card-b-heading  font-weight400  font-size-label'>Profession  :  </span><span className='employee-card-b-text  font-weight400 ing font-size-label'>{e.professional_type}</span></div>
//                                 <div className="cart-subdiv-flex" ><span className='employee-card-b-heading  font-weight400  font-size-label'>Mobile No :  </span><span className='employee-card-b-text  font-weight400 ing font-size-label'>{e.mobile}</span></div>
//                             </div>

//                         </div>


//                     </div >

//                 )
//             })}




//         </div >)
// }

const EmployeeDetailCard = ({ data, companyList, getSiteEmployee, siteDepartmentList, siteProjectList, siteSubCompanyList }) => {
    const siteRole = sessionStorage.getItem("sitemanagement_role");
    const currentUrl = window.location.href;
    const urlParts = currentUrl.split("/");
    const navigate = useNavigate();

    const Viewdetails = (emp_code) => {
        navigate(`/${urlParts[3]}/siteemployeedetail/${emp_code}/`);
    };

    const getInitials = (name) => {
        if (!name) return "?";
        const parts = name.trim().split(" ");
        return parts.length > 1
            ? (parts[0][0] + parts[1][0]).toUpperCase()
            : parts[0].slice(0, 2).toUpperCase();
    };

    const professionColors = {
        "Key Professional": { bg: "#eef2ff", text: "#4f46e5", border: "#c7d2fe" },
        "Sub Professional": { bg: "#eff6ff", text: "#2563eb", border: "#bfdbfe" },
        "Support Staff": { bg: "#f0fdf4", text: "#16a34a", border: "#bbf7d0" },
    };

    return (
        <div className='employee-cards-attractive' style={{ maxHeight: '73vh' }}>
            {data?.sort((a, b) => {
                const order = { "Key Professional": 1, "Sub Professional": 2, "Support Staff": 3 };
                return (order[a.professional_type] || 4) - (order[b.professional_type] || 4);
            }).map((e, index) => {
                const colors = professionColors[e.professional_type] || { bg: "#f8fafc", text: "#64748b", border: "#e2e8f0" };

                return (
                    <div className='employee-card-attractive' style={{ background: "linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%)" }} key={index}>
                        {/* Top accent bar */}
                        <div className="employee-card-accent" style={{ background: `linear-gradient(90deg, ${colors.text}, ${colors.text}99)` }} />

                        <div className="employee-card-top">
                            <div className="employee-avatar" style={{ background: colors.bg, color: colors.text, border: `1.5px solid ${colors.border}` }}>
                                {getInitials(e.name)}
                            </div>

                            <div className="employee-identity">
                                <h4 className="employee-name">{e.name}</h4>
                                <p className="employee-designation">{e.designation_name || "-"}</p>
                                <span className="employee-code-badge">
                                    <Hash size={10} />
                                    {e.emp_code}
                                </span>
                            </div>

                            <div className="employee-actions">
                                {siteRole === "SiteManagement" ? (
                                    <button className='employee-action-btn' onClick={() => Viewdetails(e.emp_code)} title="View Profile">
                                        <MoreVertical size={16} />
                                    </button>
                                ) : (
                                    <>
                                        {siteRole !== "ViewAccess" && (
                                            <UpdateSiteEmployee
                                                i={e}
                                                getSiteEmployee={getSiteEmployee}
                                                siteProjectList={siteProjectList}
                                                siteSubCompanyList={siteSubCompanyList}
                                                companyList={companyList}
                                            />
                                        )}
                                        {/* <button className='employee-action-btn' onClick={() => Viewdetails(e.emp_code)} title="View Profile">
                                            <MoreVertical size={16} />
                                        </button> */}
                                        {(siteRole === "Hr" || "Admin") && siteRole !== "ViewAccess" && (
                                            <DeleteSiteEmployee
                                                i={e}
                                                getSiteEmployee={getSiteEmployee}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </div>

                        <div className="employee-card-divider" />

                        <div className="employee-card-info">
                            <div className="employee-info-row">
                                <div className="employee-info-icon">
                                    <Briefcase size={12} />
                                </div>
                                <div className="employee-info-content">
                                    <span className="employee-info-label">Project</span>
                                    <span className="employee-info-value">
                                        {e.multiple_project_names?.length > 0
                                            ? e.multiple_project_names.join(", ")
                                            : e.project_short_name || "-"}
                                    </span>
                                </div>
                            </div>

                            <div className="employee-info-split">
                                <div className="employee-info-row">
                                    <div className="employee-info-icon">
                                        <Tag size={12} />
                                    </div>
                                    <div className="employee-info-content">
                                        <span className="employee-info-label">Profession</span>
                                        <span className="employee-profession-pill" style={{ background: colors.bg, color: colors.text }}>
                                            {e.professional_type || "-"}
                                        </span>
                                    </div>
                                </div>

                                <div className="employee-info-row">
                                    <div className="employee-info-icon">
                                        <Phone size={12} />
                                    </div>
                                    <div className="employee-info-content">
                                        <span className="employee-info-label">Mobile</span>
                                        <span className="employee-info-value">{e.mobile || "-"}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const AddConsultantAccount = ({ i, getConsultant, siteProjectList, companyData }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const [projectList, setProjectList] = useState([]);
    const [invoiceList, setInvoiceList] = useState([]);
    const [companyList, setCompanyList] = useState([]);
    const [fileNames, setFileNames] = useState("");

    const handleClose = () => {
        setFormData({
            company_name: "null",
            address: "",
            account_number: "",
            bank_name: "",
            ifsc_code: "",
            pan_no: "",
            gst_no: "",
            account_type: "null",
            document: "",
        });
        setFileNames("");
        setErrors({});
        setShow(false);
    };
    const handleShow = () => {
        setShow(true);
        getCompany();
    };

    const [formData, setFormData] = useState({
        company_name: "null",
        address: "",
        account_number: "",
        bank_name: "",
        ifsc_code: "",
        pan_no: "",
        gst_no: "",
        account_type: "",
        document: "",
    });

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = [
            "company_name",
            "address",
            "account_number",
            "bank_name",
            "ifsc_code",
            "pan_no",
            "gst_no",
            "document",
        ];
        requiredFields.forEach((field) => {
            if (!formData[field] || formData[field] === "null") {
                newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required!`;
            }
        });
        setErrors(newErrors);
        setIsSubmitted(true);
        return Object.keys(newErrors).length === 0;
    };

    const getCompany = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/siteproject/subcompany/`);
            setCompanyList(response.data);
        } catch (error) {
            handleErrorToast(error);
        }
    };

    const getProject = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/siteproject/projectfilterbysubcompany/${formData.company_name}/`);

            const splitCodeParts = (code) => {
                const parts = code.trim().split("/");
                const firstPart = parts.slice(0, -1).join("/");
                const lastPart = parts.pop();
                const match = lastPart.match(/^(\d+)?([A-Za-z]+)$/);
                if (match) {
                    return {
                        firstPart,
                        numericPart: match[1] ? parseInt(match[1], 10) : 0,
                        alphaPart: match[2]
                    };
                }
                return { firstPart, numericPart: 0, alphaPart: "" };
            };

            const sortedList = response.data.sort((a, b) => {
                const aParts = splitCodeParts(a.site_prcode);
                const bParts = splitCodeParts(b.site_prcode);
                if (aParts.firstPart !== bParts.firstPart) {
                    return aParts.firstPart.localeCompare(bParts.firstPart);
                }
                if (aParts.alphaPart !== bParts.alphaPart) {
                    return aParts.alphaPart.localeCompare(bParts.alphaPart);
                }
                return aParts.numericPart - bParts.numericPart;
            });

            setProjectList(sortedList);
        } catch (error) {
            handleErrorToast(error);
        }
    };

    const getInvoice = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/siteproject/consultant-invoiceBy-projectfilter/${formData.project}/null/null/`);
            setInvoiceList(response.data);
        } catch (error) {
            handleErrorToast(error);
        }
    };

    useEffect(() => {
        if (show) {
            getProject();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.company_name, show]);

    useEffect(() => {
        if (show) {
            getInvoice();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.project, show]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        const formDataa = new FormData();
        formDataa.append("company_name", formData.company_name);
        formDataa.append("address", formData.address);
        formDataa.append("account_number", formData.account_number);
        formDataa.append("bank_name", formData.bank_name);
        formDataa.append("ifsc_code", formData.ifsc_code);
        formDataa.append("pan_no", formData.pan_no);
        formDataa.append("gst_no", formData.gst_no);
        formDataa.append("account_type", formData.account_type);

        const symbols = '!@#$%^&*()_-+=';
        const lowercaseLetters = 'abcdefghijklmnopqrstuvwxyz';
        const uppercaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const numbers = '0123456789';
        const now = new Date();
        const date = now.toLocaleDateString('en-GB').split('/').reverse().join('-');
        const time = now.toLocaleTimeString('en-GB', { hour12: false }).replace(/:/g, '-');
        const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;
        let randomCode = '';
        for (let i = 0; i < 8; i++) {
            const randomIndex = Math.floor(Math.random() * allChars.length);
            randomCode += allChars[randomIndex];
        }
        const originalFile = formData.document;
        const customFileName = `Aimantra ${date} ${time}_${randomCode} ${originalFile.name}`;
        const customFile = new File([originalFile], customFileName, { type: originalFile.type });
        formDataa.append("document", customFile);

        try {
            let res = await axios.post(
                `${BASE_URL}/siteproject/consultantaccountdetails/`,
                formDataa,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                            (progressEvent.loaded * 100) / progressEvent.total
                        );
                        setUploadProgress(percentCompleted);
                    },
                }
            );

            if (res.status === 200) {
                await getConsultant();
                toast.dismiss(loadingToastId);
                toast.success("Account Details added successfully!");
                handleClose();
            } else {
                toast.dismiss(loadingToastId);
                toast.error("Failed to add data!");
            }
        } catch (err) {
            setUploadProgress(0);
            handleErrorToast(err);
        } finally {
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setInputState({ ...inputState, [name]: value.trim() ? "green" : "" });
        setFormData({ ...formData, [name]: value });
        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (value) delete updatedErrors[name];
            return updatedErrors;
        });
    };

    const handleFileChange = (e) => {
        const { name } = e.target;
        const file = e.target.files[0];

        if (file) {
            const validFileTypes = [
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            ];

            if (validFileTypes.includes(file.type)) {
                setFormData({ ...formData, [name]: file });
                setErrors({ ...errors, [name]: "" });
                setFileNames(file.name);
            } else {
                setErrors({ ...errors, [name]: "Invalid file type. Please select a PDF or DOC file." });
                setFileNames("");
            }
        } else {
            setFormData({ ...formData, [name]: null });
            setFileNames("");
        }
    };

    const handleFileClear = (name) => {
        setFormData({ ...formData, [name]: null });
        setFileNames("");
    };

    const companyOptions = companyList.map((c) => ({ value: c.id, label: c.title }));
    const handleCompanyChange = (selectedOption) => {
        handleInputChange({ target: { name: "company_name", value: selectedOption ? selectedOption.value : "null" } });
    };

    const accountTypeOptions = [
        { value: "fixed Account", label: "Fixed Account" },
        { value: "currrent Account", label: "Current Account" },
        { value: "Savings Account", label: "Savings Account" },
    ];
    const handleAccountTypeChange = (selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            account_type: selectedOption ? selectedOption.value : "null",
        }));
        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (selectedOption) delete updatedErrors.account_type;
            return updatedErrors;
        });
    };

    return (
        <>
            <button title="Add Account Details" className="btn-animated" onClick={handleShow}>
                <AddwithWhiteCircle /> Account Details
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Add" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <Tag size={22} />
                            </div>
                            <div>
                                <Modal.Title>Add Account Details</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>Add a new consultant bank account</p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

                    <Modal.Body className="modern-modal-body">
                        <div className="form-section">
                            <div className="section-title">
                                <Tag size={18} />
                                <h4>Account Details</h4>
                            </div>

                            <div className="form-grid">
                                <div className="form-field">
                                    <label><Building size={14} /> Sub Company<span className="required">*</span></label>
                                    <Select
                                        isSearchable
                                        isClearable
                                        options={companyOptions}
                                        value={companyOptions.find((option) => option.value === formData.company_name) || null}
                                        onChange={handleCompanyChange}
                                        placeholder="Select a Company"
                                        styles={CSSForSelectModern}
                                    />
                                    {errors.company_name && <span className="error-message">{errors.company_name}</span>}
                                </div>

                                <div className="form-field">
                                    <label><Landmark size={14} /> Bank Name<span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="bank_name"
                                        placeholder="Bank Name"
                                        onChange={handleInputChange}
                                        value={formData.bank_name}
                                        className={errors.bank_name ? "error" : inputState.bank_name ? "success" : ""}
                                    />
                                    {errors.bank_name && <span className="error-message">{errors.bank_name}</span>}
                                </div>

                                <div className="form-field">
                                    <label><Hash size={14} /> Account Number<span className="required">*</span></label>
                                    <input
                                        type="number"
                                        min={0}
                                        name="account_number"
                                        placeholder="Account Number"
                                        onChange={handleInputChange}
                                        value={formData.account_number}
                                        className={errors.account_number ? "error" : inputState.account_number ? "success" : ""}
                                    />
                                    {errors.account_number && <span className="error-message">{errors.account_number}</span>}
                                </div>

                                <div className="form-field">
                                    <label><CreditCard size={14} /> IFSC Code<span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="ifsc_code"
                                        placeholder="IFSC Code"
                                        onChange={handleInputChange}
                                        value={formData.ifsc_code}
                                        className={errors.ifsc_code ? "error" : inputState.ifsc_code ? "success" : ""}
                                    />
                                    {errors.ifsc_code && <span className="error-message">{errors.ifsc_code}</span>}
                                </div>

                                <div className="form-field">
                                    <label><Briefcase size={14} /> Account Type</label>
                                    <Select
                                        isSearchable
                                        isClearable
                                        options={accountTypeOptions}
                                        value={accountTypeOptions.find((option) => option.value === formData.account_type) || null}
                                        onChange={handleAccountTypeChange}
                                        placeholder="Select Account Type"
                                        styles={CSSForSelectModern}
                                    />
                                    {errors.account_type && <span className="error-message">{errors.account_type}</span>}
                                </div>

                                <div className="form-field">
                                    <label><FileText size={14} /> PAN Number<span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="pan_no"
                                        placeholder="PAN Number"
                                        onChange={handleInputChange}
                                        value={formData.pan_no}
                                        className={errors.pan_no ? "error" : inputState.pan_no ? "success" : ""}
                                    />
                                    {errors.pan_no && <span className="error-message">{errors.pan_no}</span>}
                                </div>

                                <div className="form-field">
                                    <label><FileText size={14} /> GST Number<span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="gst_no"
                                        placeholder="GST Number"
                                        onChange={handleInputChange}
                                        value={formData.gst_no}
                                        className={errors.gst_no ? "error" : inputState.gst_no ? "success" : ""}
                                    />
                                    {errors.gst_no && <span className="error-message">{errors.gst_no}</span>}
                                </div>

                                <div className="form-field">
                                    <label><MapPin size={14} /> Address<span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="address"
                                        placeholder="Address"
                                        onChange={handleInputChange}
                                        value={formData.address}
                                        className={errors.address ? "error" : inputState.address ? "success" : ""}
                                    />
                                    {errors.address && <span className="error-message">{errors.address}</span>}
                                </div>

                                <div className="form-field full-width">
                                    <label><Paperclip size={14} /> Document Upload<span className="required">*</span></label>
                                    <div className="flex-row justify-between" style={{ alignItems: 'center', gap: '10px' }}>
                                        <label
                                            htmlFor="document"
                                            className={`svg-icon ${errors.document ? 'error' : inputState.document ? 'success' : ''}`}
                                        >
                                            <div className="svg-field form-input align-center">
                                                <Attachment />
                                            </div>
                                        </label>
                                        <input
                                            id="document"
                                            type="file"
                                            name="document"
                                            accept=".pdf, .doc, .docx"
                                            onChange={handleFileChange}
                                            style={{ display: 'none' }}
                                        />
                                        {fileNames && <p className="file-name">{fileNames}</p>}
                                        {formData.document && (
                                            <button type="button" className="file-clear clear-button" onClick={() => handleFileClear("document")}>
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                    {errors.document && <span className="error-message">{errors.document}</span>}
                                </div>
                            </div>

                            <div className="form-actions">
                                <div></div>
                                <div className="submit-buttons">
                                    <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-primaryy"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={18} className="spinning" /> : <Save size={18} />}
                                        <span>{loading ? 'Adding...' : 'Add Account'}</span>
                                    </button>
                                </div>
                            </div>

                            <p className="error-message" style={{ marginTop: '12px' }}>
                                {isSubmitted && Object.keys(errors).length > 0 && (
                                    Object.keys(errors).length > 5 ? (
                                        <span>Please fill all mandatory fields!</span>
                                    ) : (
                                        Object.keys(errors).map((field) => field.charAt(0).toUpperCase() + field.slice(1)).join(', ') + ' are required!'
                                    )
                                )}
                            </p>
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};

const UpdateConsultantAccount = ({ i, getConsultant, siteProjectList, companyData }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const [projectList, setProjectList] = useState([]);
    const [invoiceList, setInvoiceList] = useState([]);
    const [companyList, setCompanyList] = useState([]);
    const [fileNames, setFileNames] = useState("");

    const handleClose = () => {
        setFormData(i);
        setFileNames("");
        setErrors({});
        setShow(false);
    };
    const handleShow = () => {
        setShow(true);
        setFormData(i);
        getCompany();
    };

    const [formData, setFormData] = useState({
        address: "",
        account_number: "",
        bank_name: "",
        ifsc_code: "",
        pan_no: "",
        gst_no: "",
        account_type: "",
    });

    const [errors, setErrors] = useState({});
    const [inputState, setInputState] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);

    const validateForm = () => {
        const newErrors = {};
        const requiredFields = [
            "address",
            "account_number",
            "bank_name",
            "ifsc_code",
            "pan_no",
            "gst_no",
        ];
        requiredFields.forEach((field) => {
            if (!formData[field] || formData[field] === "null") {
                newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required!`;
            }
        });
        setErrors(newErrors);
        setIsSubmitted(true);
        return Object.keys(newErrors).length === 0;
    };

    const getCompany = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/siteproject/subcompany/`);
            setCompanyList(response.data);
        } catch (error) {
            handleErrorToast(error);
        }
    };

    const getProject = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/siteproject/projectfilterbysubcompany/${formData.company_name}/`);

            const splitCodeParts = (code) => {
                const parts = code.trim().split("/");
                const firstPart = parts.slice(0, -1).join("/");
                const lastPart = parts.pop();
                const match = lastPart.match(/^(\d+)?([A-Za-z]+)$/);
                if (match) {
                    return {
                        firstPart,
                        numericPart: match[1] ? parseInt(match[1], 10) : 0,
                        alphaPart: match[2]
                    };
                }
                return { firstPart, numericPart: 0, alphaPart: "" };
            };

            const sortedList = response.data.sort((a, b) => {
                const aParts = splitCodeParts(a.site_prcode);
                const bParts = splitCodeParts(b.site_prcode);
                if (aParts.firstPart !== bParts.firstPart) {
                    return aParts.firstPart.localeCompare(bParts.firstPart);
                }
                if (aParts.alphaPart !== bParts.alphaPart) {
                    return aParts.alphaPart.localeCompare(bParts.alphaPart);
                }
                return aParts.numericPart - bParts.numericPart;
            });

            setProjectList(sortedList);
        } catch (error) {
            handleErrorToast(error);
        }
    };

    const getInvoice = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/siteproject/consultant-invoiceBy-projectfilter/${formData.project}/null/null/`);
            setInvoiceList(response.data);
        } catch (error) {
            handleErrorToast(error);
        }
    };

    useEffect(() => {
        if (show) {
            getProject();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.company_name]);

    useEffect(() => {
        if (show) {

            getInvoice();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.project]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        const formDataa = new FormData();
        formDataa.append("address", formData.address);
        formDataa.append("account_number", formData.account_number);
        formDataa.append("bank_name", formData.bank_name);
        formDataa.append("ifsc_code", formData.ifsc_code);
        formDataa.append("pan_no", formData.pan_no);
        formDataa.append("gst_no", formData.gst_no);
        formDataa.append("account_type", formData.account_type);

        // Only attach a new document if the user selected one; otherwise keep the existing file
        if (formData.document && typeof formData.document !== "string") {
            const symbols = '!@#$%^&*()_-+=';
            const lowercaseLetters = 'abcdefghijklmnopqrstuvwxyz';
            const uppercaseLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
            const numbers = '0123456789';
            const now = new Date();
            const date = now.toLocaleDateString('en-GB').split('/').reverse().join('-');
            const time = now.toLocaleTimeString('en-GB', { hour12: false }).replace(/:/g, '-');
            const allChars = symbols + lowercaseLetters + uppercaseLetters + numbers;
            let randomCode = '';
            for (let i = 0; i < 8; i++) {
                const randomIndex = Math.floor(Math.random() * allChars.length);
                randomCode += allChars[randomIndex];
            }
            const originalFile = formData.document;
            const customFileName = `Aimantra ${date} ${time}_${randomCode} ${originalFile.name}`;
            const customFile = new File([originalFile], customFileName, { type: originalFile.type });
            formDataa.append("document", customFile);
        }

        try {
            let res = await axios.put(
                `${BASE_URL}/siteproject/consultantaccountdetails/${i.id}/`,
                formDataa,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            if (res.status === 200) {
                await getConsultant();
                toast.dismiss(loadingToastId);
                toast.success("Account Details Updated Successfully!");
                setShow(false);
            } else {
                toast.dismiss(loadingToastId);
                toast.error("Failed to update data!");
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setInputState({ ...inputState, [name]: value.trim() ? "green" : "" });
        setFormData({ ...formData, [name]: value });
        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (value) delete updatedErrors[name];
            return updatedErrors;
        });
    };

    const handleFileChange = (e) => {
        const { name } = e.target;
        const file = e.target.files[0];

        if (file) {
            const validFileTypes = [
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            ];

            if (validFileTypes.includes(file.type)) {
                setFormData({ ...formData, [name]: file });
                setErrors({ ...errors, [name]: "" });
                setFileNames(file.name);
            } else {
                setErrors({ ...errors, [name]: "Invalid file type. Please select a PDF or DOC file." });
                setFileNames("");
            }
        } else {
            setFormData({ ...formData, [name]: null });
            setFileNames("");
        }
    };

    const handleFileClear = (name) => {
        setFormData({ ...formData, [name]: null });
        setFileNames("");
    };

    const accountTypeOptions = [
        { value: "fixed Account", label: "Fixed Account" },
        { value: "currrent Account", label: "Current Account" },
        { value: "Savings Account", label: "Savings Account" },
    ];
    const handleAccountTypeChange = (selectedOption) => {
        setFormData((prev) => ({
            ...prev,
            account_type: selectedOption ? selectedOption.value : "null",
        }));
        setErrors((prevErrors) => {
            const updatedErrors = { ...prevErrors };
            if (selectedOption) delete updatedErrors.account_type;
            return updatedErrors;
        });
    };

    return (
        <>
            <button title="Update Account Details" className="model-delete-button-modern" style={{ backgroundColor: "#00000075" }} onClick={handleShow}>
                <UpdatePencil modernButton="true" />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Update" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-project-modal" size="lg">
                    <Modal.Header className="modern-modal-header">
                        <div className="header-content">
                            <div style={{ color: "white" }}>
                                <Tag size={22} />
                            </div>
                            <div>
                                <Modal.Title>Update Account Details</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>{i.bank_name}</p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />

                    <Modal.Body className="modern-modal-body">
                        <div className="form-section">
                            <div className="section-title">
                                <Tag size={18} />
                                <h4>Account Details</h4>
                            </div>

                            <div className="form-grid">
                                <div className="form-field">
                                    <label><Landmark size={14} /> Bank Name<span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="bank_name"
                                        placeholder="Bank Name"
                                        onChange={handleInputChange}
                                        value={formData.bank_name}
                                        className={errors.bank_name ? "error" : inputState.bank_name ? "success" : ""}
                                    />
                                    {errors.bank_name && <span className="error-message">{errors.bank_name}</span>}
                                </div>

                                <div className="form-field">
                                    <label><Hash size={14} /> Account Number<span className="required">*</span></label>
                                    <input
                                        type="number"
                                        min={0}
                                        name="account_number"
                                        placeholder="Account Number"
                                        onChange={handleInputChange}
                                        value={formData.account_number}
                                        className={errors.account_number ? "error" : inputState.account_number ? "success" : ""}
                                    />
                                    {errors.account_number && <span className="error-message">{errors.account_number}</span>}
                                </div>

                                <div className="form-field">
                                    <label><CreditCard size={14} /> IFSC Code<span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="ifsc_code"
                                        placeholder="IFSC Code"
                                        onChange={handleInputChange}
                                        value={formData.ifsc_code}
                                        className={errors.ifsc_code ? "error" : inputState.ifsc_code ? "success" : ""}
                                    />
                                    {errors.ifsc_code && <span className="error-message">{errors.ifsc_code}</span>}
                                </div>

                                <div className="form-field">
                                    <label><Briefcase size={14} /> Account Type</label>
                                    <Select
                                        isSearchable
                                        isClearable
                                        options={accountTypeOptions}
                                        value={accountTypeOptions.find((option) => option.value === formData.account_type) || null}
                                        onChange={handleAccountTypeChange}
                                        placeholder="Select Account Type"
                                        styles={CSSForSelectModern}
                                    />
                                    {errors.account_type && <span className="error-message">{errors.account_type}</span>}
                                </div>

                                <div className="form-field">
                                    <label><FileText size={14} /> PAN Number<span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="pan_no"
                                        placeholder="PAN Number"
                                        onChange={handleInputChange}
                                        value={formData.pan_no}
                                        className={errors.pan_no ? "error" : inputState.pan_no ? "success" : ""}
                                    />
                                    {errors.pan_no && <span className="error-message">{errors.pan_no}</span>}
                                </div>

                                <div className="form-field">
                                    <label><FileText size={14} /> GST Number<span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="gst_no"
                                        placeholder="GST Number"
                                        onChange={handleInputChange}
                                        value={formData.gst_no}
                                        className={errors.gst_no ? "error" : inputState.gst_no ? "success" : ""}
                                    />
                                    {errors.gst_no && <span className="error-message">{errors.gst_no}</span>}
                                </div>

                                <div className="form-field full-width">
                                    <label><MapPin size={14} /> Address<span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="address"
                                        placeholder="Address"
                                        onChange={handleInputChange}
                                        value={formData.address}
                                        className={errors.address ? "error" : inputState.address ? "success" : ""}
                                    />
                                    {errors.address && <span className="error-message">{errors.address}</span>}
                                </div>

                                <div className="form-field full-width">
                                    <label><Paperclip size={14} /> Document (optional — leave blank to keep existing)</label>
                                    <div className="flex-row justify-between" style={{ alignItems: 'center', gap: '10px' }}>
                                        <label
                                            htmlFor="document"
                                            className={`svg-icon ${errors.document ? 'error' : inputState.document ? 'success' : ''}`}
                                        >
                                            <div className="svg-field form-input align-center">
                                                <Attachment />
                                            </div>
                                        </label>
                                        <input
                                            id="document"
                                            type="file"
                                            name="document"
                                            accept=".pdf, .doc, .docx"
                                            onChange={handleFileChange}
                                            style={{ display: 'none' }}
                                        />
                                        {fileNames && <p className="file-name">{fileNames}</p>}
                                        {formData.document && typeof formData.document !== "string" && (
                                            <button type="button" className="file-clear clear-button" onClick={() => handleFileClear("document")}>
                                                Clear
                                            </button>
                                        )}
                                    </div>
                                    {errors.document && <span className="error-message">{errors.document}</span>}
                                </div>
                            </div>

                            <div className="form-actions">
                                <div></div>
                                <div className="submit-buttons">
                                    <button type="button" className="btn-secondaryy" onClick={handleClose}>
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        className="btn-primaryy"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={18} className="spinning" /> : <Save size={18} />}
                                        <span>{loading ? 'Updating...' : 'Update'}</span>
                                    </button>
                                </div>
                            </div>

                            <p className="error-message" style={{ marginTop: '12px' }}>
                                {isSubmitted && Object.keys(errors).length > 0 && (
                                    Object.keys(errors).length > 5 ? (
                                        <span>Please fill all mandatory fields!</span>
                                    ) : (
                                        Object.keys(errors).map((field) => field.charAt(0).toUpperCase() + field.slice(1)).join(', ') + ' are required!'
                                    )
                                )}
                            </p>
                        </div>
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};
const DeleteAccountDetails = ({ i, getConsultant }) => {
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showProgressModal, setShowProgressModal] = useState(false);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleFormSubmitDelete = async (e) => {
        e.preventDefault();
        setShowProgressModal(true);
        setLoading(true);
        setUploadProgress(0);

        const loadingToastId = toast.loading("Loading: Please wait...");

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            if (progress >= 100) {
                progress = 100;
                clearInterval(interval);
            }
            setUploadProgress(progress);
        }, 100);

        try {
            let res = await axios.delete(`${BASE_URL}/siteproject/consultantaccountdetails/${i.id}/`);
            if (res.status === 200) {
                await getConsultant();
                toast.dismiss(loadingToastId);
                toast.success("Deleted Successfully");
                setShow(false);
            } else {
                alert(res);
            }
        } catch (err) {
            clearInterval(interval);
            setUploadProgress(0);
            handleErrorToast(err);
        } finally {
            clearInterval(interval);
            setTimeout(() => {
                setShowProgressModal(false);
            }, 1500);
            setLoading(false);
            toast.dismiss(loadingToastId);
        }
    };

    return (
        <>
            <button title="Delete Account Details" className="model-delete-button-modern" style={{ backgroundColor: "#00000075" }} onClick={handleShow}>
                <DeleteDustbin modernButton="true" />
            </button>

            {showProgressModal ? (
                <ProgressModal show={showProgressModal} progress={uploadProgress} usedFor="Delete" />
            ) : (
                <Modal show={show} onHide={handleClose} dialogClassName="modern-required-modal" centered>
                    <Modal.Header className="modern-modal-header requirement-modal-header">
                        <div className="header-content">
                            <div className="requirement-header-icon">
                                <Trash2 size={22} />
                            </div>
                            <div>
                                <Modal.Title>Delete Account Details</Modal.Title>
                                <p className="header-subtitle" style={{ color: "white" }}>
                                    This action cannot be undone
                                </p>
                            </div>
                        </div>
                        <button className="close-btn" onClick={handleClose}>
                            <X size={20} />
                        </button>
                    </Modal.Header>

                    <Modal.Body className="modern-modal-body requirement-modal-body">
                        <div className="requirement-mode-summary" style={{ marginBottom: '20px' }}>
                            <AlertCircle size={16} />
                            <span>
                                Are you sure you want to delete the account details for <strong>{i.bank_name}</strong> ({i.account_number})?
                            </span>
                        </div>

                        <div className="submit-buttons" style={{ justifyContent: 'flex-end' }}>
                            <button type="button" className="btn-secondaryy" onClick={handleClose} disabled={loading}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn-primaryy"
                                style={{ background: "#dc3545", borderColor: "#dc3545" }}
                                onClick={handleFormSubmitDelete}
                                disabled={loading}
                            >
                                {loading ? <Loader size={18} className="spinning" /> : <Trash2 size={18} />}
                                <span>{loading ? 'Deleting...' : 'Delete'}</span>
                            </button>
                        </div>

                        <ToastContainer position="top-center" autoClose={1000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
};




export {
    AddDepartment,
    UpdateDepartment,
    AddSiteDepartment,
    UpdateSiteDepartment,
    DeleteSiteDepartment,
    DeleteDepartment,
    AddSiteSubcompanys,
    UpdateSiteSubcompanyupdate,
    DeleteSiteSubcompany,

    UploadSiteEmployee,
    AddSiteEmployee,


    UpdateSiteEmployeeDesignation,

    AddBOQEmployee,
    UpdateSiteEmployee,
    DeleteSiteEmployee,


    EmployeeDetailCard,
    AddConsultantAccount,
    UpdateConsultantAccount,
    DeleteAccountDetails
};
